import { useStatelessChat } from '@platform/chat';
import type { DriveDocumentListItem } from '@platform/documents';
import { DriveDocumentSearch } from '@platform/documents';
import { setActiveConversation } from '@platform/timeline';
import { LLM_MODEL_OPTIONS, type SupportedProvider } from '@shared/constants/llmModels';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Icon,
  IconButton,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@shared/ui';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { tokenService } from '../../services/tokenService';
import type { RootState } from '../../store/store';
import { ResultFormatter } from './formatters/ResultFormatter';
import InspectorPanel from './InspectorPanel';
import PhaseAccordion from './PhaseAccordion';
import useAgentStreaming, { type ExecutionStep, type StreamingMessage } from './useAgentStreaming';

const TEST_PROMPTS = [
  {
    name: 'Quarterly OKRs',
    prompt: 'Find docs about quarterly OKRs and objectives',
  },
  {
    name: 'Meeting Notes',
    prompt: 'Show me meeting notes from last week with Sarah',
  },
  {
    name: 'Budget Docs',
    prompt: 'Find all documents mentioning budget over $100k',
  },
  { name: 'Project Status', prompt: 'Find recent project status updates' },
  { name: 'Team Retros', prompt: 'Show retrospective documents from Q4' },
  { name: 'Weekly Boss Update', prompt: 'Tell my boss what I did this week' },
];

const getProviderModels = (provider: string) =>
  LLM_MODEL_OPTIONS[provider as SupportedProvider] ?? [];

const OPENAI_MODEL_OPTIONS = getProviderModels('openai');
const ANTHROPIC_MODEL_OPTIONS = getProviderModels('anthropic');

const DEFAULT_OPENAI_MODEL = OPENAI_MODEL_OPTIONS[0] ?? '';
const DEFAULT_ANTHROPIC_MODEL = ANTHROPIC_MODEL_OPTIONS[0] ?? '';
const DEFAULT_PLANNER_MODEL = OPENAI_MODEL_OPTIONS[0] ?? '';
const DEFAULT_EXECUTOR_MODEL = OPENAI_MODEL_OPTIONS[1] ?? OPENAI_MODEL_OPTIONS[0] ?? '';

interface AgentStepDescriptor {
  id: string;
  name?: string;
  description?: string;
  version?: string;
  tags?: string[];
  supports_streaming?: boolean;
  attributes?: Record<string, string>;
  input_schema?: any;
  output_schema?: any;
}

const NATIVE_CHAT_STEP_ENDPOINT = '/rpc/v1/agent/native/steps/agent.native.chat';

const prettyPrint = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return value;
    }
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const generateExampleFromSchema = (schema: any): any => {
  if (!schema || typeof schema !== 'object') {
    return {};
  }

  if (schema.example !== undefined) return schema.example;
  if (schema.default !== undefined) return schema.default;
  if (schema.const !== undefined) return schema.const;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) return schema.enum[0];

  if (Array.isArray(schema.oneOf) && schema.oneOf.length > 0) {
    return generateExampleFromSchema(schema.oneOf[0]);
  }
  if (Array.isArray(schema.anyOf) && schema.anyOf.length > 0) {
    return generateExampleFromSchema(schema.anyOf[0]);
  }
  if (Array.isArray(schema.allOf) && schema.allOf.length > 0) {
    return schema.allOf.reduce((acc: any, part: any) => {
      const fragment = generateExampleFromSchema(part);
      if (typeof fragment === 'object' && fragment !== null && !Array.isArray(fragment)) {
        return { ...acc, ...fragment };
      }
      return fragment ?? acc;
    }, {});
  }

  const type = Array.isArray(schema.type) ? schema.type[0] : schema.type;

  switch (type) {
    case 'object': {
      const props = schema.properties ?? {};
      const result: Record<string, any> = {};
      Object.keys(props).forEach((key) => {
        result[key] = generateExampleFromSchema(props[key]);
      });
      return result;
    }
    case 'array': {
      const item = generateExampleFromSchema(schema.items ?? {});
      return [item];
    }
    case 'number':
    case 'integer':
      if (schema.minimum !== undefined) return schema.minimum;
      if (schema.maximum !== undefined) return schema.maximum;
      return 0;
    case 'boolean':
      return false;
    case 'string':
    default: {
      if (schema.format === 'date-time') {
        return new Date().toISOString();
      }
      if (schema.format === 'date') {
        return new Date().toISOString().split('T')[0];
      }
      if (
        schema.format === 'uuid' ||
        (typeof schema.pattern === 'string' && /uuid/i.test(schema.pattern))
      ) {
        return '00000000-0000-4000-8000-000000000000';
      }
      return '';
    }
  }
};

interface ToolDefinition {
  name: string;
  description: string;
  type: 'custom' | 'builtin' | 'drive';
  enabled: boolean;
  schema?: any;
  category?: string;
}

// Database tool type (from /rpc/v1/tools)
interface DBTool {
  id: string;
  name: string;
  description: string;
  json_schema: any;
  category?: string;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  created_by?: string | null;
  base_url?: string | null;
}

// Database workflow (short) used for create/edit UI
interface DBWorkflowShort {
  id: string;
  key: string;
  name: string;
  description?: string;
}

interface ContextInjection {
  source: string;
  target: string;
  format?: string;
  required?: boolean;
}

interface WorkflowStep {
  id: string;
  name: string;
  type: string;
  agentType?: string;
  description?: string;
  prompt?: string; // The actual system prompt sent to the AI
  provider?: string;
  plannerModel?: string;
  executorModel?: string;
  allowedTools?: string[];
  resultKey?: string;
  mergeResultIntoContext?: boolean;
  contextInjections?: ContextInjection[];
  status: string;
  result?: any;
  error?: string;
  startedAt?: string;
  completedAt?: string;
  dependencies?: string[]; // For dependency tracking
  streamingEvents?: WorkflowStreamEvent[];
}

interface WorkflowStreamEvent {
  type: string;
  data: any;
}

interface WorkflowExecution {
  id: string;
  workflowId: string;
  goal: string;
  status: string;
  currentStep?: string;
  steps: WorkflowStep[];
  results: Record<string, any>;
  error?: string;
  startedAt: string;
  completedAt?: string;
}

type PlannerExecutorProvider = 'openai' | 'anthropic';

interface StepDraft {
  id?: string;
  name: string;
  agentType: string;
  provider: PlannerExecutorProvider;
  plannerModel: string;
  executorModel: string;
  prompt: string;
  tools: string[];
}

const createEmptyStep = (): StepDraft => ({
  name: '',
  agentType: 'researcher',
  provider: 'openai',
  plannerModel: DEFAULT_PLANNER_MODEL || 'o4-mini',
  executorModel: DEFAULT_EXECUTOR_MODEL || DEFAULT_PLANNER_MODEL || 'gpt-4o',
  prompt: '',
  tools: [],
});

const summarizeWorkflowStreaming = (
  events?: WorkflowStreamEvent[]
): {
  steps: ExecutionStep[];
  text: string;
  error?: string;
  active: boolean;
} => {
  const steps: ExecutionStep[] = [];
  const toolEntries = new Map<string, ExecutionStep>();
  const textParts: string[] = [];
  let errorMessage: string | undefined;
  let active = true;

  (events ?? []).forEach((evt, index) => {
    if (!evt || typeof evt.type !== 'string') return;
    console.log('[STREAM_EVENT]', evt.type, evt.data);
    const timestamp = index;
    const data = evt.data;

    switch (evt.type) {
      case 'planner_decision': {
        const iteration = data?.iteration ?? data?.decision?.iteration ?? index;
        steps.push({
          id: `planner-${index}`,
          type: 'planner_decision',
          status: 'complete',
          timestamp,
          iteration,
          data,
        });
        break;
      }
      case 'planning.start': {
        steps.push({
          id: `planning-start-${index}`,
          type: 'planner_decision',
          status: 'complete',
          timestamp,
          iteration: 0,
          data: { decision: 'start', ...data },
        });
        break;
      }
      case 'planning.iteration': {
        const iteration = data?.iteration ?? index;
        steps.push({
          id: `planning-iter-${index}`,
          type: 'planner_decision',
          status: 'complete',
          timestamp,
          iteration,
          data,
        });
        break;
      }
      case 'planning.complete': {
        steps.push({
          id: `planning-complete-${index}`,
          type: 'planner_decision',
          status: 'complete',
          timestamp,
          data: { decision: 'complete', ...data },
        });
        break;
      }
      case 'tool_call': {
        const name = data?.tool?.name ?? data?.name ?? `tool-${index}`;
        const entry: ExecutionStep = {
          id: `tool-${index}`,
          type: 'tool_call',
          status: 'in_progress',
          timestamp,
          iteration: data?.iteration,
          data: data?.tool ?? data,
        };
        steps.push(entry);
        toolEntries.set(name, entry);
        break;
      }
      case 'tool_result': {
        const name = data?.tool ?? data?.name ?? `tool-${index}`;
        const entry = toolEntries.get(name);
        const status: ExecutionStep['status'] = data?.error ? 'error' : 'complete';
        if (entry) {
          entry.status = status;
          entry.duration = data?.duration ?? entry.duration;
          entry.data = { ...(entry.data ?? {}), ...data };
        } else {
          steps.push({
            id: `tool-result-${index}`,
            type: 'tool_result',
            status,
            timestamp,
            data,
          });
        }
        if (data?.error && typeof data.error === 'string') {
          errorMessage = data.error;
        }
        break;
      }
      case 'executor_content': {
        const textValue = typeof data === 'string' ? data : (data?.text ?? '');
        if (textValue) {
          textParts.push(textValue);
        }
        steps.push({
          id: `executor-${index}`,
          type: 'executor_content',
          status: 'complete',
          timestamp,
          data,
        });
        break;
      }
      case 'error': {
        const message =
          typeof data?.error === 'string' ? data.error : typeof data === 'string' ? data : '';
        if (message) {
          errorMessage = message;
        }
        break;
      }
      case 'final': {
        active = false;
        const directive = data?.finalDirective;
        if (typeof directive === 'string' && directive.trim()) {
          textParts.push(directive);
        }
        break;
      }
      case 'end': {
        active = false;
        break;
      }
      default:
        break;
    }
  });

  return {
    steps,
    text: textParts.join(''),
    error: errorMessage,
    active,
  };
};

// Database prompt types
interface DBPrompt {
  id: string;
  key: string;
  name: string;
  description?: string;
  category?: string;
}

interface DBPromptVersionActive {
  id: string;
  prompt_id: string;
  version: number;
  content: string;
  is_active: boolean;
}

const AVAILABLE_TOOLS: ToolDefinition[] = [
  {
    name: 'drive_search_documents',
    description: 'Search Google Drive/Docs for the user',
    type: 'drive',
    enabled: true,
    category: 'Drive',
    schema: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Search query for documents' },
        relevant_only: {
          type: 'boolean',
          default: false,
          description: 'Only return 1-on-1 relevant documents',
        },
        meet_transcripts: {
          type: 'boolean',
          default: false,
          description: 'Include meeting transcripts',
        },
        limit: {
          type: 'integer',
          minimum: 1,
          maximum: 100,
          default: 20,
          description: 'Maximum number of documents to return',
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'web_search',
    description: 'Search the web for information',
    type: 'builtin',
    enabled: false,
    category: 'Web',
    schema: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Search query' },
        max_results: {
          type: 'integer',
          default: 5,
          description: 'Maximum number of results',
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'code_interpreter',
    description: 'Execute Python code and return results',
    type: 'builtin',
    enabled: false,
    category: 'Code',
    schema: {
      type: 'object',
      properties: {
        code: { type: 'string', description: 'Python code to execute' },
        timeout: {
          type: 'integer',
          default: 30,
          description: 'Timeout in seconds',
        },
      },
      required: ['code'],
    },
  },
  {
    name: 'file_search',
    description: 'Search through uploaded files',
    type: 'builtin',
    enabled: false,
    category: 'Files',
    schema: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Search query' },
        file_ids: {
          type: 'array',
          items: { type: 'string' },
          description: 'File IDs to search',
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'team_guess',
    description:
      "Guess who is on the user's team based on Drive collaboration signals (document ownership, permissions, activity, comments)",
    type: 'drive',
    enabled: true,
    category: 'Team',
    schema: {
      type: 'object',
      properties: {
        person_id: {
          type: 'string',
          description: 'Optional person ID to guess team for (defaults to authenticated user)',
        },
      },
    },
  },
];

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  provider?: string;
  model?: string;
  toolCalls?: any[];
  documents?: any[];
  latency?: number;
  error?: string;
  isStreaming?: boolean;
  streamingContent?: string;
  toolCallsInProgress?: string[];
  plannerIteration?: number;
  plannerDecision?: any;
  executionTrace?: ExecutionStep[];
}

interface TestRun {
  id: string;
  timestamp: number;
  provider: string;
  model: string;
  prompt: string;
  success: boolean;
  latency: number;
  toolCallsCount: number;
  documentsCount: number;
  executionTrace?: ExecutionStep[];
  error?: string;
}

export default function AgentsTestPage(): React.ReactElement {
  const dispatch = useDispatch();
  const personId = useSelector((s: RootState) => s.auth.user?.id) || '';

  // Basic config
  const [provider, setProvider] = React.useState<string>('openai');
  const [model, setModel] = React.useState<string>(DEFAULT_OPENAI_MODEL);
  const [message, setMessage] = React.useState<string>('Find docs about quarterly plan');

  // Tools configuration
  const [availableTools, setAvailableTools] = React.useState<ToolDefinition[]>(AVAILABLE_TOOLS);
  const [customTools, setCustomTools] = React.useState<ToolDefinition[]>([]);
  const [toolDialogOpen, setToolDialogOpen] = React.useState(false);
  const [newToolName, setNewToolName] = React.useState('');
  const [newToolDesc, setNewToolDesc] = React.useState('');
  const [newToolSchema, setNewToolSchema] = React.useState('{}');
  const [toolTestSelection, setToolTestSelection] = React.useState<string>('');
  const [toolTestPayload, setToolTestPayload] = React.useState<string>('{}');
  const [toolTestResponse, setToolTestResponse] = React.useState<string>('');
  const [toolTestLoading, setToolTestLoading] = React.useState<boolean>(false);
  const [toolTestError, setToolTestError] = React.useState<string | null>(null);
  const [toolTestMeta, setToolTestMeta] = React.useState<{
    method: string;
    url: string;
  } | null>(null);

  // Workflow builder state
  const [builderMode, setBuilderMode] = React.useState<'create' | 'edit'>('create');
  const [builderWorkflowId, setBuilderWorkflowId] = React.useState<string | null>(null);
  const [builderVersionId, setBuilderVersionId] = React.useState<string | null>(null);
  const [builderKey, setBuilderKey] = React.useState<string>('');
  const [builderName, setBuilderName] = React.useState<string>('');
  const [builderDescription, setBuilderDescription] = React.useState<string>('');
  const [builderGoal, setBuilderGoal] = React.useState<string>('');
  const [builderDefaultProvider, setBuilderDefaultProvider] =
    React.useState<PlannerExecutorProvider>('openai');
  const [builderDefaultModel, setBuilderDefaultModel] =
    React.useState<string>(DEFAULT_OPENAI_MODEL);
  const [builderSteps, setBuilderSteps] = React.useState<StepDraft[]>([]);
  const [stepDialogOpen, setStepDialogOpen] = React.useState<boolean>(false);
  const [stepEditIndex, setStepEditIndex] = React.useState<number | null>(null);
  const [stepDraft, setStepDraft] = React.useState<StepDraft>(createEmptyStep);
  const [stepMenuAnchor, setStepMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [stepMenuIndex, setStepMenuIndex] = React.useState<number | null>(null);
  const [stepInsertBeforeIndex, setStepInsertBeforeIndex] = React.useState<number | null>(null);

  // Conversation state
  const [conversation, setConversation] = React.useState<Message[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [agentSteps, setAgentSteps] = React.useState<AgentStepDescriptor[]>([]);
  const [agentStepsLoading, setAgentStepsLoading] = React.useState(false);
  const [agentStepsError, setAgentStepsError] = React.useState<string | null>(null);

  // Comparison mode
  const [compareMode, setCompareMode] = React.useState(false);
  const [compareProvider, setCompareProvider] = React.useState<string>('anthropic');
  const [compareModel, setCompareModel] = React.useState<string>(DEFAULT_ANTHROPIC_MODEL);
  const [compareResult, setCompareResult] = React.useState<Message | null>(null);
  void compareProvider; // suppress unused warning
  void compareModel; // suppress unused warning
  void compareResult; // suppress unused warning
  void setCompareProvider; // suppress unused warning
  void setCompareModel; // suppress unused warning
  void compareMode; // suppress unused warning
  void setCompareMode; // suppress unused warning

  // Mock mode
  const [mockMode, setMockMode] = React.useState(false);
  const {
    isStreaming,
    executionSteps: streamingSteps,
    currentMessage: streamingMessage,
    error: streamingError,
    startStream,
    stopStream,
    resetStream,
  } = useAgentStreaming();
  const [streamingEnabled, setStreamingEnabled] = React.useState(true);
  const [pendingAssistant, setPendingAssistant] = React.useState<StreamingMessage | null>(null);
  const streamingSupported = streamingEnabled && !mockMode;

  React.useEffect(() => {
    setPendingAssistant(streamingMessage);
  }, [streamingMessage]);

  React.useEffect(() => () => resetStream(), [resetStream]);

  React.useEffect(() => {
    if (!streamingEnabled) {
      stopStream();
      resetStream();
      setPendingAssistant(null);
    }
  }, [resetStream, stopStream, streamingEnabled]);

  const fetchAgentSteps = React.useCallback(async () => {
    try {
      setAgentStepsLoading(true);
      setAgentStepsError(null);
      const headers = await tokenService.buildAuthHeaders();
      const res = await fetch('/rpc/v1/agent/native/steps', {
        method: 'GET',
        headers,
      });
      if (!res.ok) {
        throw new Error(`Failed to load agent steps (${res.status})`);
      }
      const data = await res.json();
      const steps = Array.isArray(data.steps) ? (data.steps as AgentStepDescriptor[]) : [];
      setAgentSteps(steps);
    } catch (err: any) {
      console.error('Failed to load agent steps', err);
      setAgentStepsError(err?.message ?? 'Failed to load agent steps');
    } finally {
      setAgentStepsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!personId) {
      setAgentSteps([]);
      setAgentStepsError('Login required');
      setAgentStepsLoading(false);
      return;
    }
    void fetchAgentSteps();
  }, [fetchAgentSteps, personId]);

  // Metrics
  const [testRuns, setTestRuns] = React.useState<TestRun[]>([]);

  // Workflow state (DB-backed)
  const [workflows, setWorkflows] = React.useState<DBWorkflowShort[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = React.useState<string>('');
  const [_workflowGoal, setWorkflowGoal] = React.useState<string>('');
  const [testUserPrompt, setTestUserPrompt] = React.useState<string>('');
  const [attachedDocuments, setAttachedDocuments] = React.useState<DriveDocumentListItem[]>([]);
  const [docSearchLoading, setDocSearchLoading] = React.useState(false);
  const [docSearchError, setDocSearchError] = React.useState<string | null>(null);
  const [workflowExecution, setWorkflowExecution] = React.useState<WorkflowExecution | null>(null);
  const [workflowLoading, setWorkflowLoading] = React.useState(false);
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const [showTestForm, setShowTestForm] = React.useState(false);

  // Chat conversation ID for WebSocket streaming
  const [chatConversationId] = React.useState<string>(
    () => `chat-${Date.now()}-${Math.random().toString(36).substring(7)}`
  );

  // Initialize Redux timeline for this conversation
  React.useEffect(() => {
    dispatch(setActiveConversation(chatConversationId));
  }, [dispatch, chatConversationId]);

  // Inspector panel state
  const [inspectorOpen, setInspectorOpen] = React.useState(false);
  const [selectedStep, setSelectedStep] = React.useState<ExecutionStep | null>(null);
  const [editingStepId, setEditingStepId] = React.useState<string | null>(null);
  const [editedResult, setEditedResult] = React.useState<string>('');
  const [expandedResults, setExpandedResults] = React.useState<Set<string>>(new Set());
  const [planStepDrafts, setPlanStepDrafts] = React.useState<Record<string, string>>({});
  const [copiedDraftKey, setCopiedDraftKey] = React.useState<string | null>(null);
  const [selectedWorkflowStepId, setSelectedWorkflowStepId] = React.useState<string | null>(null);

  // Prompt editing state
  const [editedPrompts, setEditedPrompts] = React.useState<Record<string, string>>({});
  const [promptEditDialogOpen, setPromptEditDialogOpen] = React.useState(false);
  const [editingPromptStepId, setEditingPromptStepId] = React.useState<string | null>(null);
  const [tempPromptEdit, setTempPromptEdit] = React.useState<string>('');
  const [_stepsWithEditedPrompts, setStepsWithEditedPrompts] = React.useState<Set<string>>(
    new Set()
  );

  React.useEffect(() => {
    if (!copiedDraftKey) return;
    const timeout = window.setTimeout(() => setCopiedDraftKey(null), 2000);
    return () => window.clearTimeout(timeout);
  }, [copiedDraftKey]);

  // Cleanup polling on unmount
  React.useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // UI state
  const [activeTab, setActiveTab] = React.useState(0);
  const [_savePromptDialog, setSavePromptDialog] = React.useState(false);
  void setSavePromptDialog; // suppress unused warning
  const [customPrompts, setCustomPrompts] = React.useState<typeof TEST_PROMPTS>([]);

  // Connect to SSE for workflow planning events (only when workflow is selected)
  useStatelessChat({
    convId: selectedWorkflow ? chatConversationId : null,
    enabled: !!selectedWorkflow && activeTab === 1,
  });

  // Get timeline entities from Redux for the chat conversation
  const timelineEntities = useSelector((state: RootState) => {
    const convData = state.timeline?.byConvId?.[chatConversationId];
    if (!convData) return [];
    return convData.order.map((id) => convData.byId[id]).filter(Boolean);
  });
  void timelineEntities; // suppress unused warning (used in workflow tab, not chat tab)

  // DB prompts state
  const [dbPrompts, setDbPrompts] = React.useState<DBPrompt[]>([]);
  const [dbPromptsLoading, setDbPromptsLoading] = React.useState<boolean>(false);
  const [dbPromptsError, setDbPromptsError] = React.useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = React.useState<boolean>(false);
  const [createDialogOpen, setCreateDialogOpen] = React.useState<boolean>(false);
  const [selectedDbPrompt, setSelectedDbPrompt] = React.useState<DBPrompt | null>(null);
  const [editMetaForm, setEditMetaForm] = React.useState<{
    name: string;
    description: string;
    category: string;
  } | null>(null);
  const [editOriginalMeta, setEditOriginalMeta] = React.useState<{
    name: string;
    description: string;
    category: string;
  } | null>(null);
  const [editContent, setEditContent] = React.useState<string>('');
  const [editOriginalContent, setEditOriginalContent] = React.useState<string>('');
  const [editSaving, setEditSaving] = React.useState<boolean>(false);

  const [createForm, setCreateForm] = React.useState<{
    key: string;
    name: string;
    description: string;
    category: string;
    content: string;
    changelog: string;
  }>({
    key: '',
    name: '',
    description: '',
    category: '',
    content: '',
    changelog: 'Initial version from Agents page',
  });
  const [createSaving, setCreateSaving] = React.useState<boolean>(false);

  // DB tools state for Tools tab
  const [dbTools, setDbTools] = React.useState<DBTool[]>([]);
  const [dbToolsLoading, setDbToolsLoading] = React.useState<boolean>(false);
  const [dbToolsError, setDbToolsError] = React.useState<string | null>(null);
  const [toolEditDialogOpen, setToolEditDialogOpen] = React.useState<boolean>(false);
  const [toolCreateDialogOpen, setToolCreateDialogOpen] = React.useState<boolean>(false);
  const [selectedDbTool, setSelectedDbTool] = React.useState<DBTool | null>(null);
  const formatTimestamp = (iso?: string | null): string => {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
      return iso;
    }
    return date.toLocaleString();
  };

  const [toolForm, setToolForm] = React.useState<{
    id?: string;
    name: string;
    description: string;
    category: string;
    baseUrl: string;
    enabled: boolean;
    jsonText: string;
    createdAt?: string;
    updatedAt?: string;
    createdBy?: string | null;
  }>({
    name: '',
    description: '',
    category: '',
    baseUrl: '',
    enabled: true,
    jsonText: '{}',
  });
  const [toolSaving, setToolSaving] = React.useState<boolean>(false);

  const computeToolExample = React.useCallback(
    (toolName: string) => {
      const dbTool = dbTools.find((tool) => tool.name === toolName);
      const custom = [...availableTools, ...customTools].find((tool) => tool.name === toolName);
      const schema = dbTool?.json_schema ?? custom?.schema;
      let example = generateExampleFromSchema(schema);
      if (!example || typeof example !== 'object' || Array.isArray(example)) {
        example = {};
      }

      const fallbackPerson = personId || '<person_id>';

      switch (toolName) {
        case 'doclens':
          example = {
            op: 'search_related',
            query: 'Weekly summary documents',
            top_k: 20,
            person_id: fallbackPerson,
          };
          break;
        case 'identity_lookup':
          example = {
            person_id: fallbackPerson,
          };
          break;
        case 'team_guess':
          example = {
            person_id: fallbackPerson,
          };
          break;
        case 'drive_search_documents':
          example = {
            query: 'Quarterly plan',
            relevant_only: false,
            meet_transcripts: false,
            limit: 20,
          };
          break;
        case 'drive_activity':
          example = {
            start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            end: new Date().toISOString(),
            limit: 100,
          };
          break;
        case 'drive_write_document':
          example = {
            title: 'Weekly Summary',
            markdown: '# Summary\nProvide highlights here.',
          };
          break;
        default:
          break;
      }

      return example;
    },
    [availableTools, customTools, dbTools, personId]
  );

  React.useEffect(() => {
    if (!toolTestSelection) return;
    const example = computeToolExample(toolTestSelection);
    try {
      setToolTestPayload(JSON.stringify(example ?? {}, null, 2));
    } catch {
      setToolTestPayload('{}');
    }
    setToolTestResponse('');
    setToolTestError(null);
    setToolTestMeta(null);
  }, [toolTestSelection, computeToolExample]);

  const toolTestOptions = React.useMemo(() => {
    const known = [
      'doclens',
      'identity_lookup',
      'team_guess',
      'drive_search_documents',
      'drive_write_document',
      'drive_activity',
    ];
    const names = new Set<string>(known);
    for (const tool of dbTools) names.add(tool.name);
    for (const tool of availableTools) names.add(tool.name);
    for (const tool of customTools) names.add(tool.name);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [availableTools, customTools, dbTools]);

  const runToolTest = React.useCallback(async () => {
    if (!toolTestSelection) {
      setToolTestError('Select a tool to test.');
      return;
    }

    let payload: any = {};
    if (toolTestPayload.trim().length > 0) {
      try {
        payload = JSON.parse(toolTestPayload);
      } catch (err: any) {
        setToolTestError(`Invalid JSON payload: ${err?.message || String(err)}`);
        return;
      }
    }

    setToolTestLoading(true);
    setToolTestError(null);
    setToolTestMeta(null);
    setToolTestResponse('');

    try {
      const headers = await tokenService.buildAuthHeaders();
      let method: 'GET' | 'POST' = 'POST';
      let url = '';
      let body: any = payload;
      let skipBody = false;
      const authHeaders = { ...headers };

      switch (toolTestSelection) {
        case 'doclens': {
          url = '/rpc/v1/tools/doclens';
          body = { ...payload };
          if (!body.person_id && personId) body.person_id = personId;
          break;
        }
        case 'identity_lookup': {
          url = '/rpc/v1/tools/identity_lookup';
          body = { ...payload };
          if (!body.person_id && personId) body.person_id = personId;
          break;
        }
        case 'team_guess': {
          url = '/rpc/v1/team.guess';
          body = { ...payload };
          if (!body.person_id && personId) body.person_id = personId;
          break;
        }
        case 'drive_search_documents': {
          if (!personId) {
            throw new Error('A person ID is required to test drive_search_documents.');
          }
          const params = new URLSearchParams();
          if (payload.query) params.set('q', String(payload.query));
          if (payload.relevant_only) params.set('relevant_only', 'true');
          if (payload.meet_transcripts) params.set('meet_transcripts', 'true');
          if (payload.limit !== undefined) params.set('limit', String(payload.limit));
          url = `/rpc/v1/drive1on1/users/${encodeURIComponent(personId)}/providers/google-drive/documents`;
          const qs = params.toString();
          if (qs) url += `?${qs}`;
          method = 'GET';
          skipBody = true;
          break;
        }
        case 'drive_write_document': {
          if (!personId) {
            throw new Error('A person ID is required to test drive_write_document.');
          }
          url = `/rpc/v1/drive1on1/users/${encodeURIComponent(personId)}/providers/google-drive/documents`;
          body = {
            title: payload.title ?? 'Untitled Document',
            markdown: payload.markdown ?? '# Summary',
            folderId: payload.folderId ?? '',
          };
          break;
        }
        case 'drive_activity': {
          if (!personId) {
            throw new Error('A person ID is required to test drive_activity.');
          }
          const params = new URLSearchParams();
          if (payload.start) params.set('start', String(payload.start));
          if (payload.end) params.set('end', String(payload.end));
          if (payload.limit !== undefined) params.set('limit', String(payload.limit));
          if (Array.isArray(payload.fileIds) && payload.fileIds.length > 0) {
            params.set('fileIds', payload.fileIds.join(','));
          }
          if (payload.cursor) params.set('cursor', String(payload.cursor));
          url = `/rpc/v1/drive1on1/users/${encodeURIComponent(personId)}/providers/google-drive/activity`;
          const qs = params.toString();
          if (qs) url += `?${qs}`;
          method = 'GET';
          skipBody = true;
          break;
        }
        default: {
          const tool = dbTools.find((t) => t.name === toolTestSelection);
          if (tool?.base_url) {
            url = new URL(`/rpc/v1/tools/${tool.name}`, tool.base_url).toString();
          } else {
            throw new Error(`Tool "${toolTestSelection}" is not yet supported in the tester.`);
          }
          break;
        }
      }

      setToolTestMeta({ method, url });

      const requestInit: RequestInit = {
        method,
        headers: authHeaders,
      };

      if (!skipBody) {
        requestInit.body = JSON.stringify(body ?? {});
      }

      const res = await fetch(url, requestInit);
      const text = await res.text();
      let formatted = text;
      try {
        formatted = JSON.stringify(JSON.parse(text), null, 2);
      } catch {
        formatted = text;
      }

      if (!res.ok) {
        throw new Error(formatted || `${res.status} ${res.statusText}`);
      }

      setToolTestResponse(formatted || 'No response body.');
    } catch (err: any) {
      setToolTestError(err?.message || String(err));
      setToolTestResponse('');
    } finally {
      setToolTestLoading(false);
    }
  }, [toolTestSelection, toolTestPayload, personId, dbTools]);

  const resetToolTestPayload = React.useCallback(() => {
    if (!toolTestSelection) return;
    const example = computeToolExample(toolTestSelection);
    try {
      setToolTestPayload(JSON.stringify(example ?? {}, null, 2));
    } catch {
      setToolTestPayload('{}');
    }
    setToolTestResponse('');
    setToolTestError(null);
    setToolTestMeta(null);
  }, [computeToolExample, toolTestSelection]);

  // Load saved state from localStorage
  React.useEffect(() => {
    const savedPrompts = localStorage.getItem('agent-custom-prompts');
    const savedTools = localStorage.getItem('agent-available-tools');
    const savedCustomTools = localStorage.getItem('agent-custom-tools');

    if (savedPrompts) {
      try {
        setCustomPrompts(JSON.parse(savedPrompts));
      } catch (e) {
        console.error('Failed to load custom prompts', e);
      }
    }
    if (savedTools) {
      try {
        setAvailableTools(JSON.parse(savedTools));
      } catch (e) {
        console.error('Failed to load tools', e);
      }
    }
    if (savedCustomTools) {
      try {
        setCustomTools(JSON.parse(savedCustomTools));
      } catch (e) {
        console.error('Failed to load custom tools', e);
      }
    }
  }, []);

  React.useEffect(() => {
    const first = getProviderModels(provider)[0];
    if (first) setModel(first);
  }, [provider]);

  React.useEffect(() => {
    const first = getProviderModels(compareProvider)[0];
    if (first) setCompareModel(first);
  }, [compareProvider]);

  React.useEffect(() => {
    const models = getProviderModels(builderDefaultProvider);
    const preferred = builderDefaultModel || models[0] || '';
    if (preferred) {
      setModel(preferred);
    }
    setProvider(builderDefaultProvider);
  }, [builderDefaultProvider, builderDefaultModel]);

  React.useEffect(() => {
    setWorkflowGoal(builderGoal);
  }, [builderGoal]);

  const resetBuilder = React.useCallback(() => {
    setBuilderMode('create');
    setBuilderWorkflowId(null);
    setBuilderKey('');
    setBuilderName('');
    setBuilderDescription('');
    setBuilderGoal('');
    setBuilderDefaultProvider('openai');
    setBuilderDefaultModel(DEFAULT_OPENAI_MODEL);
    setBuilderSteps([]);
    setStepEditIndex(null);
    setStepDraft(createEmptyStep());
    setSelectedWorkflow('');
    setAttachedDocuments([]);
  }, []);

  const openStepEditor = React.useCallback(
    (index: number | null = null) => {
      if (index !== null) {
        const existing = builderSteps[index];
        if (existing) {
          setStepDraft({ ...existing });
          setStepEditIndex(index);
        }
      } else {
        setStepDraft(createEmptyStep());
        setStepEditIndex(null);
      }
      setStepInsertBeforeIndex(null); // Reset insert before index
      setStepDialogOpen(true);
    },
    [builderSteps]
  );

  const saveStepDraft = React.useCallback(async () => {
    if (!stepDraft.name.trim()) {
      alert('Step name is required');
      return;
    }
    if (!stepDraft.prompt.trim()) {
      alert('Step prompt is required');
      return;
    }

    try {
      if (
        builderMode === 'edit' &&
        builderWorkflowId &&
        builderVersionId &&
        stepEditIndex !== null &&
        stepDraft.id
      ) {
        const headers = await tokenService.buildAuthHeaders();
        const jsonHeaders = {
          ...headers,
          'Content-Type': 'application/json',
        } as HeadersInit;
        const res = await fetch(
          `/rpc/v1/workflow-versions/${builderVersionId}/steps/${stepDraft.id}`,
          {
            method: 'PUT',
            headers: jsonHeaders,
            body: JSON.stringify({
              name: stepDraft.name,
              type: 'agent',
              agent_type: stepDraft.agentType,
              provider: stepDraft.provider,
              model: stepDraft.executorModel,
              planner_model: stepDraft.plannerModel,
              executor_model: stepDraft.executorModel,
              prompt: stepDraft.prompt,
              tools: stepDraft.tools,
            }),
          }
        );
        if (!res.ok) {
          throw new Error(`Failed to update step (${res.status})`);
        }
      }

      setBuilderSteps((prev) => {
        const next = [...prev];
        if (stepEditIndex !== null && stepEditIndex >= 0 && stepEditIndex < prev.length) {
          // Editing existing step
          next[stepEditIndex] = { ...stepDraft };
        } else if (
          stepInsertBeforeIndex !== null &&
          stepInsertBeforeIndex >= 0 &&
          stepInsertBeforeIndex <= prev.length
        ) {
          // Inserting before a specific index
          next.splice(stepInsertBeforeIndex, 0, { ...stepDraft });
        } else {
          // Adding at the end
          next.push({ ...stepDraft });
        }
        return next;
      });
      setStepDialogOpen(false);
      setStepDraft(createEmptyStep());
      setStepEditIndex(null);
      setStepInsertBeforeIndex(null);

      // Optionally refresh from backend if needed; local state already reflects the change
    } catch (e: any) {
      alert(e?.message || 'Failed to save step');
    }
  }, [
    stepDraft,
    stepEditIndex,
    stepInsertBeforeIndex,
    builderMode,
    builderWorkflowId,
    builderVersionId,
  ]);

  const deleteStep = React.useCallback((index: number) => {
    setBuilderSteps((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const openInsertBeforeEditor = React.useCallback((index: number) => {
    setStepDraft(createEmptyStep());
    setStepEditIndex(null);
    setStepInsertBeforeIndex(index);
    setStepDialogOpen(true);
  }, []);

  const fetchDbPrompts = React.useCallback(async () => {
    try {
      setDbPromptsLoading(true);
      setDbPromptsError(null);
      const headers = await tokenService.buildAuthHeaders();
      const res = await fetch('/rpc/v1/prompts', { headers });
      if (!res.ok) throw new Error(`Failed to load prompts (${res.status})`);
      const data = await res.json();
      setDbPrompts(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setDbPromptsError(err?.message || 'Failed to load prompts');
    } finally {
      setDbPromptsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (activeTab === 2) {
      fetchDbPrompts();
    }
  }, [activeTab, fetchDbPrompts]);

  const fetchDbTools = React.useCallback(async () => {
    try {
      setDbToolsLoading(true);
      setDbToolsError(null);
      const headers = await tokenService.buildAuthHeaders();
      const res = await fetch('/rpc/v1/tools', { headers });
      if (!res.ok) throw new Error(`Failed to load tools (${res.status})`);
      const data = await res.json();
      const tools = Array.isArray(data) ? data : [];
      setDbTools(tools);

      // Convert database tools to ToolDefinition format and merge with hardcoded tools
      const dbToolDefs: ToolDefinition[] = tools.map((dbTool: DBTool) => ({
        name: dbTool.name,
        description: dbTool.description,
        type: 'custom' as const,
        enabled: dbTool.enabled,
        schema: dbTool.json_schema,
        category: dbTool.category,
      }));

      // Merge: database tools override hardcoded tools with same name
      const mergedTools = [...AVAILABLE_TOOLS];
      for (const dbTool of dbToolDefs) {
        const existingIndex = mergedTools.findIndex((t) => t.name === dbTool.name);
        if (existingIndex >= 0) {
          mergedTools[existingIndex] = dbTool;
        } else {
          mergedTools.push(dbTool);
        }
      }
      setAvailableTools(mergedTools);
    } catch (err: any) {
      setDbToolsError(err?.message || 'Failed to load tools');
    } finally {
      setDbToolsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    // Load tools on mount and when switching to Agentic (0), Chat (1), or Tools (3) tabs
    if (activeTab === 0 || activeTab === 1 || activeTab === 3) {
      fetchDbTools();
    }
  }, [activeTab, fetchDbTools]);

  // Listen for tools-changed events from other components
  React.useEffect(() => {
    const handleToolsChanged = () => {
      fetchDbTools();
    };
    window.addEventListener('tools-changed', handleToolsChanged);
    return () => window.removeEventListener('tools-changed', handleToolsChanged);
  }, [fetchDbTools]);

  const openEditTool = React.useCallback((tool: DBTool) => {
    setSelectedDbTool(tool);
    setToolForm({
      id: tool.id,
      name: tool.name || '',
      description: tool.description || '',
      category: tool.category || '',
      baseUrl: tool.base_url || '',
      enabled: !!tool.enabled,
      jsonText: JSON.stringify(tool.json_schema ?? {}, null, 2),
      createdAt: tool.created_at,
      updatedAt: tool.updated_at,
      createdBy: tool.created_by,
    });
    setToolEditDialogOpen(true);
  }, []);

  const saveEditTool = React.useCallback(async () => {
    if (!selectedDbTool) return;
    try {
      setToolSaving(true);
      let parsed: any = {};
      try {
        parsed = toolForm.jsonText.trim() ? JSON.parse(toolForm.jsonText) : {};
      } catch (_e) {
        alert('Invalid JSON schema');
        setToolSaving(false);
        return;
      }
      const headers = await tokenService.buildAuthHeaders();
      const res = await fetch(`/rpc/v1/tools/${selectedDbTool.id}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: toolForm.name,
          description: toolForm.description,
          category: toolForm.category,
          enabled: toolForm.enabled,
          base_url: toolForm.baseUrl,
          json_schema: parsed,
        }),
      });
      if (!res.ok) throw new Error(`Failed to update tool (${res.status})`);
      await fetchDbTools();
      // Notify other components that tools have changed
      window.dispatchEvent(new CustomEvent('tools-changed'));
      setToolEditDialogOpen(false);
      setSelectedDbTool(null);
    } catch (err: any) {
      alert(err?.message || 'Failed to save tool');
    } finally {
      setToolSaving(false);
    }
  }, [selectedDbTool, toolForm, fetchDbTools]);

  const openCreateTool = React.useCallback(() => {
    setToolForm({
      name: '',
      description: '',
      category: '',
      baseUrl: '',
      enabled: true,
      jsonText: '{}',
    });
    setToolCreateDialogOpen(true);
  }, []);

  const saveCreateTool = React.useCallback(async () => {
    if (!toolForm.name) {
      alert('Name is required');
      return;
    }
    try {
      setToolSaving(true);
      let parsed: any = {};
      try {
        parsed = toolForm.jsonText.trim() ? JSON.parse(toolForm.jsonText) : {};
      } catch (_e) {
        alert('Invalid JSON schema');
        setToolSaving(false);
        return;
      }
      const headers = await tokenService.buildAuthHeaders();
      const res = await fetch(`/rpc/v1/tools`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: toolForm.name,
          description: toolForm.description,
          category: toolForm.category,
          enabled: toolForm.enabled,
          base_url: toolForm.baseUrl,
          json_schema: parsed,
        }),
      });
      if (!res.ok) throw new Error(`Failed to create tool (${res.status})`);
      await fetchDbTools();
      // Notify other components that tools have changed
      window.dispatchEvent(new CustomEvent('tools-changed'));
      setToolCreateDialogOpen(false);
    } catch (err: any) {
      alert(err?.message || 'Failed to create tool');
    } finally {
      setToolSaving(false);
    }
  }, [toolForm, fetchDbTools]);

  // @ts-expect-error - Intentionally unused for future use
  const _loadDbPrompt = React.useCallback(async (promptId: string) => {
    try {
      const headers = await tokenService.buildAuthHeaders();
      const res = await fetch(`/rpc/v1/prompts/${promptId}/versions/active`, {
        headers,
      });
      if (!res.ok) throw new Error('Failed to load prompt content');
      const version: DBPromptVersionActive = await res.json();
      if (version?.content) {
        setMessage(version.content);
      } else {
        alert('No active version content for this prompt');
      }
    } catch (e: any) {
      alert(e?.message || 'Failed to load prompt content');
    }
  }, []);

  const openEditPrompt = React.useCallback(async (prompt: DBPrompt) => {
    setSelectedDbPrompt(prompt);
    setEditDialogOpen(true);
    // Prefill meta
    const meta = {
      name: prompt.name || '',
      description: prompt.description || '',
      category: prompt.category || '',
    };
    setEditMetaForm(meta);
    setEditOriginalMeta(meta);
    // Load active content
    try {
      const headers = await tokenService.buildAuthHeaders();
      const res = await fetch(`/rpc/v1/prompts/${prompt.id}/versions/active`, {
        headers,
      });
      if (res.ok) {
        const version: DBPromptVersionActive = await res.json();
        const content = version?.content || '';
        setEditContent(content);
        setEditOriginalContent(content);
      } else {
        setEditContent('');
        setEditOriginalContent('');
      }
    } catch (_e) {
      setEditContent('');
      setEditOriginalContent('');
    }
  }, []);

  const saveEditPrompt = React.useCallback(async () => {
    if (!selectedDbPrompt || !editMetaForm) return;
    try {
      setEditSaving(true);
      const headers = await tokenService.buildAuthHeaders();
      const jsonHeaders = {
        ...headers,
        'Content-Type': 'application/json',
      } as HeadersInit;

      // Update metadata if changed
      if (
        !editOriginalMeta ||
        editOriginalMeta.name !== editMetaForm.name ||
        (editOriginalMeta.description || '') !== (editMetaForm.description || '') ||
        (editOriginalMeta.category || '') !== (editMetaForm.category || '')
      ) {
        const resMeta = await fetch(`/rpc/v1/prompts/${selectedDbPrompt.id}`, {
          method: 'PUT',
          headers: jsonHeaders,
          body: JSON.stringify({
            name: editMetaForm.name,
            description: editMetaForm.description,
            category: editMetaForm.category,
          }),
        });
        if (!resMeta.ok) throw new Error(`Failed to update prompt (${resMeta.status})`);
      }

      // Create new version if content changed
      if (editContent !== editOriginalContent) {
        const resVer = await fetch(`/rpc/v1/prompts/${selectedDbPrompt.id}/versions`, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({
            content: editContent,
            changelog: 'Edited from Agents page',
          }),
        });
        if (!resVer.ok) throw new Error(`Failed to create version (${resVer.status})`);
        const version = await resVer.json();
        // Activate new version
        const resAct = await fetch(
          `/rpc/v1/prompts/${selectedDbPrompt.id}/versions/${version.id}/activate`,
          {
            method: 'POST',
            headers: headers,
          }
        );
        if (!resAct.ok) throw new Error(`Failed to activate version (${resAct.status})`);
      }

      // Refresh list and close
      await fetchDbPrompts();
      setEditDialogOpen(false);
      setSelectedDbPrompt(null);
    } catch (e: any) {
      alert(e?.message || 'Failed to save prompt');
    } finally {
      setEditSaving(false);
    }
  }, [
    selectedDbPrompt,
    editMetaForm,
    editOriginalMeta,
    editContent,
    editOriginalContent,
    fetchDbPrompts,
  ]);

  const openCreatePrompt = React.useCallback(() => {
    setCreateForm({
      key: '',
      name: '',
      description: '',
      category: '',
      content: '',
      changelog: 'Initial version from Agents page',
    });
    setCreateDialogOpen(true);
  }, []);

  const saveCreatePrompt = React.useCallback(async () => {
    if (!createForm.key || !createForm.name) {
      alert('Key and Name are required');
      return;
    }
    try {
      setCreateSaving(true);
      const headers = await tokenService.buildAuthHeaders();
      const jsonHeaders = {
        ...headers,
        'Content-Type': 'application/json',
      } as HeadersInit;

      const resCreate = await fetch(`/rpc/v1/prompts`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({
          key: createForm.key,
          name: createForm.name,
          description: createForm.description,
          category: createForm.category,
        }),
      });
      if (!resCreate.ok) throw new Error(`Failed to create prompt (${resCreate.status})`);
      const created: DBPrompt = await resCreate.json();

      // Create first version if content provided
      if (createForm.content && created?.id) {
        const resVer = await fetch(`/rpc/v1/prompts/${created.id}/versions`, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({
            content: createForm.content,
            changelog: createForm.changelog || 'Initial',
          }),
        });
        if (!resVer.ok) throw new Error(`Failed to create version (${resVer.status})`);
        const version = await resVer.json();
        // Activate it
        await fetch(`/rpc/v1/prompts/${created.id}/versions/${version.id}/activate`, {
          method: 'POST',
          headers,
        });
      }

      await fetchDbPrompts();
      setCreateDialogOpen(false);
    } catch (e: any) {
      alert(e?.message || 'Failed to create prompt');
    } finally {
      setCreateSaving(false);
    }
  }, [createForm, fetchDbPrompts]);

  // @ts-expect-error - Intentionally unused for future use
  const _toggleTool = (toolName: string) => {
    const updated = availableTools.map((t) =>
      t.name === toolName ? { ...t, enabled: !t.enabled } : t
    );
    setAvailableTools(updated);
    localStorage.setItem('agent-available-tools', JSON.stringify(updated));
  };

  // @ts-expect-error - Intentionally unused for future use
  const _toggleCustomTool = (toolName: string) => {
    const updated = customTools.map((t) =>
      t.name === toolName ? { ...t, enabled: !t.enabled } : t
    );
    setCustomTools(updated);
    localStorage.setItem('agent-custom-tools', JSON.stringify(updated));
  };

  const addCustomTool = () => {
    if (!newToolName.trim()) return;

    let schema: any;
    try {
      schema = JSON.parse(newToolSchema);
    } catch (_e) {
      alert('Invalid JSON schema');
      return;
    }

    const newTool: ToolDefinition = {
      name: newToolName.trim(),
      description: newToolDesc.trim() || 'Custom tool',
      type: 'custom',
      enabled: true,
      schema,
      category: 'Custom',
    };

    const updated = [...customTools, newTool];
    setCustomTools(updated);
    localStorage.setItem('agent-custom-tools', JSON.stringify(updated));

    setToolDialogOpen(false);
    setNewToolName('');
    setNewToolDesc('');
    setNewToolSchema('{}');
  };

  // @ts-expect-error - Intentionally unused for future use
  const _deleteCustomTool = (toolName: string) => {
    const updated = customTools.filter((t) => t.name !== toolName);
    setCustomTools(updated);
    localStorage.setItem('agent-custom-tools', JSON.stringify(updated));
  };

  const getEnabledTools = React.useCallback((): ToolDefinition[] => {
    return [...availableTools, ...customTools].filter((t) => t.enabled);
  }, [availableTools, customTools]);

  const executeBlocking = React.useCallback(
    async (
      promptText: string,
      targetProvider: string,
      targetModel: string,
      runCompareFlag: boolean,
      startTime: number
    ): Promise<void> => {
      try {
        const enabledTools = getEnabledTools();
        const headers = await tokenService.buildAuthHeaders();
        const res = await fetch(NATIVE_CHAT_STEP_ENDPOINT, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            provider: targetProvider,
            model: targetModel,
            message: promptText,
            personId,
            tools: enabledTools,
          }),
        });

        const latency = Date.now() - startTime;
        if (!res.ok) {
          throw new Error(`Agent error (${res.status})`);
        }

        const data = await res.json();
        const msg =
          Array.isArray(data.messages) && data.messages[0]?.content ? data.messages[0].content : '';
        const calls = Array.isArray(data.toolCalls) ? data.toolCalls : [];
        const docs = Array.isArray(data?.results?.documents) ? data.results.documents : [];

        const assistantMessage: Message = {
          role: 'assistant',
          content: msg,
          timestamp: Date.now(),
          provider: targetProvider,
          model: targetModel,
          toolCalls: calls,
          documents: docs,
          latency,
        };

        if (runCompareFlag) {
          setCompareResult(assistantMessage);
        } else {
          setConversation((prev) => [...prev, assistantMessage]);
        }

        const testRun: TestRun = {
          id: `${Date.now()}`,
          timestamp: Date.now(),
          provider: targetProvider,
          model: targetModel,
          prompt: promptText,
          success: true,
          latency,
          toolCallsCount: calls.length,
          documentsCount: docs.length,
        };
        setTestRuns((prev) => [...prev, testRun]);
      } catch (err: any) {
        const latency = Date.now() - startTime;
        const errorText = err?.message || 'Request failed';
        const errorMessage: Message = {
          role: 'assistant',
          content: `Error: ${errorText}`,
          timestamp: Date.now(),
          provider: targetProvider,
          model: targetModel,
          error: errorText,
          latency,
        };

        if (runCompareFlag) {
          setCompareResult(errorMessage);
        } else {
          setConversation((prev) => [...prev, errorMessage]);
        }

        const testRun: TestRun = {
          id: `${Date.now()}`,
          timestamp: Date.now(),
          provider: targetProvider,
          model: targetModel,
          prompt: promptText,
          success: false,
          latency,
          toolCallsCount: 0,
          documentsCount: 0,
          error: errorText,
        };
        setTestRuns((prev) => [...prev, testRun]);
      }
    },
    [getEnabledTools, personId]
  );

  const runAgent = React.useCallback(
    async (customMessage?: string, runCompare = false) => {
      if (!personId) {
        const errorMsg: Message = {
          role: 'assistant',
          content: 'Error: Missing person ID (not logged in?)',
          timestamp: Date.now(),
          error: 'Missing person ID',
        };
        setConversation((prev) => [...prev, errorMsg]);
        return;
      }

      const promptText = customMessage || message;
      const targetProvider = runCompare ? compareProvider : provider;
      const targetModel = runCompare ? compareModel : model;

      setPendingAssistant(null);
      resetStream();

      const userMessage: Message = {
        role: 'user',
        content: promptText,
        timestamp: Date.now(),
      };
      setConversation((prev) => [...prev, userMessage]);
      setLoading(true);

      const startTime = Date.now();

      try {
        if (mockMode) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          const mockResponse: Message = {
            role: 'assistant',
            content: 'Mock response: I found 3 documents about quarterly plan.',
            timestamp: Date.now(),
            provider: targetProvider,
            model: targetModel,
            latency: Date.now() - startTime,
            toolCalls: [
              {
                name: 'drive_search_documents',
                args: { query: 'quarterly plan' },
              },
            ],
            documents: [
              {
                id: '1',
                name: 'Q4 2024 OKRs.docx',
                modifiedTime: '2024-10-01',
              },
              {
                id: '2',
                name: 'Quarterly Planning.pdf',
                modifiedTime: '2024-09-15',
              },
              { id: '3', name: 'Q3 Review.docx', modifiedTime: '2024-09-30' },
            ],
          };
          setConversation((prev) => [...prev, mockResponse]);

          const testRun: TestRun = {
            id: `${Date.now()}`,
            timestamp: Date.now(),
            provider: targetProvider,
            model: targetModel,
            prompt: promptText,
            success: true,
            latency: mockResponse.latency!,
            toolCallsCount: 1,
            documentsCount: 3,
          };
          setTestRuns((prev) => [...prev, testRun]);
          return;
        }

        if (streamingSupported) {
          try {
            const streamResult = await startStream({
              provider: targetProvider,
              model: targetModel,
              message: promptText,
              personId,
              tools: getEnabledTools(),
            });

            const finalMessage = streamResult.finalMessage ?? {
              role: 'assistant',
              content: '',
              timestamp: Date.now(),
              provider: targetProvider,
              model: targetModel,
            };
            const latency = finalMessage.latency ?? Date.now() - startTime;
            const toolCalls = Array.isArray(streamResult.payload?.toolCalls)
              ? streamResult.payload.toolCalls
              : Array.isArray(finalMessage.toolCalls)
                ? finalMessage.toolCalls
                : [];
            const documents = Array.isArray(streamResult.payload?.results?.documents)
              ? streamResult.payload.results.documents
              : Array.isArray(finalMessage.documents)
                ? finalMessage.documents
                : [];

            const assistantMessage: Message = {
              role: 'assistant',
              content: finalMessage.content,
              timestamp: Date.now(),
              provider: targetProvider,
              model: targetModel,
              toolCalls,
              documents,
              latency,
              error: finalMessage.error,
              executionTrace: streamResult.executionSteps,
            };

            if (runCompare) {
              setCompareResult(assistantMessage);
            } else {
              setConversation((prev) => [...prev, assistantMessage]);
            }

            const testRun: TestRun = {
              id: `${Date.now()}`,
              timestamp: Date.now(),
              provider: targetProvider,
              model: targetModel,
              prompt: promptText,
              success: !assistantMessage.error,
              latency,
              toolCallsCount: Array.isArray(toolCalls) ? toolCalls.length : 0,
              documentsCount: Array.isArray(documents) ? documents.length : 0,
              executionTrace: streamResult.executionSteps,
              error: assistantMessage.error,
            };
            setTestRuns((prev) => [...prev, testRun]);
            resetStream();
            setPendingAssistant(null);
            return;
          } catch (streamErr) {
            console.warn('Streaming attempt failed, falling back to blocking request', streamErr);
            resetStream();
            setPendingAssistant(null);
            const fallbackStart = Date.now();
            await executeBlocking(
              promptText,
              targetProvider,
              targetModel,
              runCompare,
              fallbackStart
            );
            return;
          }
        }

        await executeBlocking(promptText, targetProvider, targetModel, runCompare, startTime);
      } finally {
        setLoading(false);
      }
    },
    [
      compareModel,
      compareProvider,
      executeBlocking,
      getEnabledTools,
      message,
      model,
      mockMode,
      personId,
      provider,
      resetStream,
      startStream,
      streamingSupported,
    ]
  );

  const runComparison = React.useCallback(async () => {
    setCompareResult(null);
    await runAgent(undefined, false);
    await runAgent(undefined, true);
  }, [runAgent]);

  // Helper to merge workflow execution while preserving streaming events
  const mergeExecutionPreservingEvents = React.useCallback(
    (newExecution: WorkflowExecution, prevExecution: WorkflowExecution | null) => {
      if (!prevExecution) return newExecution;

      const mergedSteps = newExecution.steps.map((newStep) => {
        const prevStep = prevExecution.steps.find((s) => s.id === newStep.id);
        if (prevStep?.streamingEvents) {
          return {
            ...newStep,
            streamingEvents: prevStep.streamingEvents,
          };
        }
        return newStep;
      });

      return {
        ...newExecution,
        steps: mergedSteps,
      };
    },
    []
  );

  const startPolling = React.useCallback(
    (executionId: string) => {
      // Clear any existing polling interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      const pollInterval = setInterval(async () => {
        try {
          console.log('[POLL] Fetching workflow status...', executionId);
          const headers = await tokenService.buildAuthHeaders();
          const res = await fetch(`/rpc/v1/agent/workflow/${executionId}`, {
            method: 'GET',
            headers,
          });

          if (!res.ok) {
            console.error('[POLL] Failed to poll workflow status:', res.status);
            return;
          }

          const execution: WorkflowExecution = await res.json();
          console.log('[POLL] Received update:', {
            status: execution.status,
            steps: execution.steps?.map((s) => ({
              id: s.id,
              name: s.name,
              status: s.status,
            })),
          });

          // Preserve streaming events from previous state when updating from polling
          setWorkflowExecution((prev) => mergeExecutionPreservingEvents(execution, prev));

          // Stop polling if workflow is complete or failed
          if (execution.status === 'completed' || execution.status === 'failed') {
            console.log('[POLL] Workflow finished, stopping polling');
            clearInterval(pollInterval);
            pollingIntervalRef.current = null;
            setWorkflowLoading(false);
          }
        } catch (err: any) {
          console.error('[POLL] Polling error:', err);
          // Don't stop polling on transient errors
        }
      }, 2000); // Poll every 2 seconds

      pollingIntervalRef.current = pollInterval;

      // Stop polling after 10 minutes max (prioritizing success over speed)
      setTimeout(() => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setWorkflowLoading(false);
      }, 600000);
    },
    [mergeExecutionPreservingEvents]
  );

  const runWorkflowChat = React.useCallback(async () => {
    const goal = message.trim();
    console.log('runWorkflowChat called', {
      selectedWorkflow,
      message: goal,
      personId,
    });
    if (!selectedWorkflow || !goal || !personId) {
      return;
    }

    setLoading(true);
    try {
      const headers = await tokenService.buildAuthHeaders();
      const allowedModels = getProviderModels(provider);
      const effectiveModel = allowedModels.includes(model) ? model : allowedModels[0] || model;

      const body: Record<string, any> = {
        workflowId: selectedWorkflow,
        goal,
        provider,
        model: effectiveModel,
        personId,
        conversationId: chatConversationId,
      };
      const enabledTools = getEnabledTools();
      if (enabledTools.length > 0) {
        body.tools = enabledTools;
      }

      const res = await fetch('/rpc/v1/agent/workflow/start', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let detail = '';
        try {
          const errJson = await res.json();
          detail = errJson?.error_description || errJson?.error || '';
        } catch {
          // ignore
        }
        throw new Error(
          detail ? `Failed to start workflow: ${detail}` : 'Failed to start workflow'
        );
      }

      const execution: WorkflowExecution = await res.json();
      setWorkflowExecution(execution);

      const userMessage: Message = {
        role: 'user',
        content: goal,
        timestamp: Date.now(),
      };
      setConversation((prev) => [...prev, userMessage]);
      setMessage('');

      const runRes = await fetch(`/rpc/v1/agent/workflow/${execution.id}/run`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });

      if (!runRes.ok) {
        let detail = '';
        try {
          const errJson = await runRes.json();
          detail = errJson?.error_description || errJson?.error || '';
        } catch {
          // ignore
        }
        throw new Error(
          detail ? `Failed to execute workflow: ${detail}` : 'Failed to execute workflow'
        );
      }

      startPolling(execution.id);

      console.log(`Workflow ${execution.id} started, events streaming via ${chatConversationId}`);
    } catch (err: any) {
      console.error('Failed to start workflow:', err);
      const errorMsg: Message = {
        role: 'assistant',
        content: `Error: ${err.message}`,
        timestamp: Date.now(),
        error: err.message,
      };
      setConversation((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  }, [
    selectedWorkflow,
    message,
    personId,
    provider,
    model,
    chatConversationId,
    getEnabledTools,
    startPolling,
  ]);
  void runWorkflowChat; // suppress unused warning (used in workflow tab, not chat tab)

  const clearConversation = () => {
    setConversation([]);
    setCompareResult(null);
    setPendingAssistant(null);
    resetStream();
  };

  const saveCustomPrompt = () => {
    const name = prompt('Prompt name:');
    if (!name) return;

    const newPrompt = { name, prompt: message };
    const updated = [...customPrompts, newPrompt];
    setCustomPrompts(updated);
    localStorage.setItem('agent-custom-prompts', JSON.stringify(updated));
    setSavePromptDialog(false);
  };

  const loadPrompt = (prompt: string) => {
    setMessage(prompt);
  };

  const exportResults = (format: 'json' | 'markdown') => {
    if (format === 'json') {
      const data = { conversation, testRuns };
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agent-test-${Date.now()}.json`;
      a.click();
    } else {
      let markdown = `# Agent Test Results\n\n`;
      markdown += `Generated: ${new Date().toLocaleString()}\n\n`;
      markdown += `## Conversation\n\n`;

      conversation.forEach((msg) => {
        markdown += `### ${msg.role === 'user' ? '👤 User' : '🤖 Assistant'}\n`;
        if (msg.model) markdown += `*Model: ${msg.provider}/${msg.model}*\n`;
        if (msg.latency) markdown += `*Latency: ${msg.latency}ms*\n`;
        markdown += `\n${msg.content}\n\n`;
        if (msg.toolCalls && msg.toolCalls.length > 0) {
          markdown += `**Tool Calls:**\n`;
          msg.toolCalls.forEach((call) => {
            markdown += `- ${call.name}: ${JSON.stringify(call.args)}\n`;
          });
          markdown += `\n`;
        }
        if (msg.documents && msg.documents.length > 0) {
          markdown += `**Documents Found:** ${msg.documents.length}\n\n`;
        }
      });

      const blob = new Blob([markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `agent-test-${Date.now()}.md`;
      a.click();
    }
  };

  // Workflow functions
  const loadWorkflows = React.useCallback(async () => {
    try {
      const headers = await tokenService.buildAuthHeaders();
      const res = await fetch('/rpc/v1/workflows', { method: 'GET', headers });
      if (!res.ok) throw new Error('Failed to load workflows');
      const data = await res.json();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.workflows)
          ? data.workflows
          : [];
      setWorkflows(list as DBWorkflowShort[]);
    } catch (err: any) {
      console.error('Failed to load workflows:', err);
    }
  }, []);

  const handleDocumentsSelected = React.useCallback((docs: DriveDocumentListItem[]) => {
    if (!docs || docs.length === 0) return;
    setAttachedDocuments((prev) => {
      const existingById = new Map(prev.map((doc) => [doc.documentId, doc]));
      const next = [...prev];
      docs.forEach((doc) => {
        if (!doc?.documentId) return;
        if (existingById.has(doc.documentId)) {
          const idx = next.findIndex((item) => item.documentId === doc.documentId);
          if (idx >= 0) {
            next[idx] = { ...existingById.get(doc.documentId)!, ...doc };
          }
        } else {
          next.push(doc);
        }
      });
      return next;
    });
    setDocSearchError(null);
  }, []);

  const handleRemoveDocument = React.useCallback((documentId: string) => {
    setAttachedDocuments((prev) => prev.filter((doc) => doc.documentId !== documentId));
  }, []);

  const loadWorkflowForEdit = React.useCallback(async (workflowId: string) => {
    try {
      const headers = await tokenService.buildAuthHeaders();
      const wfRes = await fetch(`/rpc/v1/workflows/${workflowId}`, {
        method: 'GET',
        headers,
      });
      if (!wfRes.ok) throw new Error(`Failed to load workflow (${wfRes.status})`);
      const wfData = await wfRes.json();
      let version = wfData?.version;
      let versionId = version?.id;
      if (!versionId) {
        // No active version: try to load the latest existing version (if any)
        const versionsRes = await fetch(`/rpc/v1/workflows/${workflowId}/versions`, {
          method: 'GET',
          headers,
        });
        if (versionsRes.ok) {
          const versions = await versionsRes.json();
          if (Array.isArray(versions) && versions.length > 0) {
            const latest = versions.reduce((acc: any, v: any) => {
              if (!acc) return v;
              return (v?.version ?? 0) > (acc?.version ?? 0) ? v : acc;
            }, null as any);
            version = latest ?? null;
            versionId = latest?.id;
          }
        }
      }

      let versionData: any = { steps: [] };
      if (versionId) {
        const versionRes = await fetch(`/rpc/v1/workflow-versions/${versionId}`, {
          method: 'GET',
          headers,
        });
        if (!versionRes.ok) throw new Error(`Failed to load workflow steps (${versionRes.status})`);
        versionData = await versionRes.json();
      }

      setBuilderMode('edit');
      setBuilderWorkflowId(wfData.id || workflowId);
      setBuilderVersionId(versionId || null);
      setBuilderKey(wfData.key || '');
      setBuilderName(version?.name || wfData.name || '');
      setBuilderDescription(version?.description || wfData.description || '');
      const goalTemplate = version?.goal_template || '';
      setBuilderGoal(goalTemplate);
      setWorkflowGoal(goalTemplate);

      const defaultProvider = (version?.default_provider || 'openai') as PlannerExecutorProvider;
      setBuilderDefaultProvider(defaultProvider);
      const defaultModel = version?.default_model || DEFAULT_OPENAI_MODEL;
      setBuilderDefaultModel(defaultModel);

      const steps = Array.isArray(versionData?.steps) ? versionData.steps : [];
      const mappedSteps: StepDraft[] = steps.map((s: any): StepDraft => {
        const config = s?.config ?? {};
        const promptFromConfig = typeof config.prompt === 'string' ? config.prompt : '';
        let prompt = promptFromConfig;
        if (!prompt && Array.isArray(s?.prompts) && s.prompts.length > 0) {
          const firstPrompt = s.prompts[0];
          if (firstPrompt?.content) {
            prompt = firstPrompt.content;
          }
        }

        const providerValue = (config.provider ||
          s?.provider ||
          defaultProvider ||
          'openai') as PlannerExecutorProvider;
        const plannerValue = config.planner_model || DEFAULT_PLANNER_MODEL;
        const executorValue =
          config.executor_model || s?.model || defaultModel || DEFAULT_EXECUTOR_MODEL;
        const toolsValue = Array.isArray(config.tools)
          ? config.tools.filter((t: any) => typeof t === 'string')
          : [];

        return {
          id: s?.id,
          name: s?.name || '',
          agentType: s?.agent_type || 'researcher',
          provider: providerValue,
          plannerModel: plannerValue,
          executorModel: executorValue,
          prompt,
          tools: toolsValue as string[],
        };
      });

      setBuilderSteps(mappedSteps);
      setSelectedWorkflow(wfData.key || '');
    } catch (err: any) {
      console.error('Failed to load workflow for edit', err);
      alert(err?.message || 'Failed to load workflow');
    }
  }, []);

  const saveWorkflowDefinition = React.useCallback(async () => {
    if (!builderKey.trim() || !builderName.trim()) {
      alert('Workflow key and name are required');
      return;
    }
    if (builderSteps.length === 0) {
      alert('Add at least one step to the workflow');
      return;
    }
    if (!builderGoal.trim()) {
      alert('Workflow goal/ System Prompt');
      return;
    }

    setWorkflowLoading(true);
    try {
      const headers = await tokenService.buildAuthHeaders();
      const jsonHeaders = {
        ...headers,
        'Content-Type': 'application/json',
      } as HeadersInit;

      let workflowId = builderWorkflowId;
      if (builderMode === 'create' || !workflowId) {
        const res = await fetch('/rpc/v1/workflows', {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({
            key: builderKey,
            name: builderName,
            description: builderDescription,
            is_template: true,
          }),
        });
        if (!res.ok) throw new Error(`Failed to create workflow (${res.status})`);
        const wf = await res.json();
        workflowId = wf.id;
        setBuilderWorkflowId(workflowId);
      } else {
        await fetch(`/rpc/v1/workflows/${workflowId}`, {
          method: 'PUT',
          headers: jsonHeaders,
          body: JSON.stringify({
            name: builderName,
            description: builderDescription,
            is_template: true,
          }),
        });
      }

      if (!workflowId) throw new Error('Workflow identifier missing after save');

      const versionRes = await fetch(`/rpc/v1/workflows/${workflowId}/versions`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({
          name: builderName,
          description: builderDescription,
          goal_template: builderGoal,
          max_retries: 2,
          timeout_seconds: 900,
          default_provider: builderDefaultProvider,
          default_model: builderDefaultModel,
          changelog:
            builderMode === 'edit' ? 'Updated via Agents UI' : 'Initial version via Agents UI',
        }),
      });
      if (!versionRes.ok)
        throw new Error(`Failed to create workflow version (${versionRes.status})`);
      const version = await versionRes.json();
      const versionId = version.id;

      for (const step of builderSteps) {
        const stepRes = await fetch(`/rpc/v1/workflow-versions/${versionId}/steps`, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({
            name: step.name,
            type: 'agent',
            agent_type: step.agentType,
            provider: step.provider,
            model: step.executorModel,
            planner_model: step.plannerModel,
            executor_model: step.executorModel,
            prompt: step.prompt,
            tools: step.tools,
          }),
        });
        if (!stepRes.ok) {
          throw new Error(`Failed to create step "${step.name}" (${stepRes.status})`);
        }
      }

      await fetch(`/rpc/v1/workflows/${workflowId}/versions/${versionId}/activate`, {
        method: 'POST',
        headers,
      });

      setBuilderMode('edit');
      setBuilderWorkflowId(workflowId);
      setSelectedWorkflow(builderKey);
      setWorkflowGoal(builderGoal);
      await loadWorkflows();
      await loadWorkflowForEdit(workflowId);
      alert('Workflow saved successfully');
    } catch (err: any) {
      console.error('Failed to save workflow', err);
      alert(err?.message || 'Failed to save workflow');
    } finally {
      setWorkflowLoading(false);
    }
  }, [
    builderKey,
    builderName,
    builderDescription,
    builderGoal,
    builderDefaultProvider,
    builderDefaultModel,
    builderSteps,
    builderMode,
    builderWorkflowId,
    loadWorkflowForEdit,
    loadWorkflows,
  ]);

  // Fetch steps for workflow key via /rpc/v1 (map key->id, then active version -> steps)
  React.useEffect(() => {
    if (activeTab === 0) {
      loadWorkflows();
    }
  }, [activeTab, loadWorkflows]);

  // Auto-select first running/pending step when workflow execution loads or updates
  React.useEffect(() => {
    if (!workflowExecution?.steps || workflowExecution.steps.length === 0) {
      setSelectedWorkflowStepId(null);
      return;
    }

    const currentlySelected = workflowExecution.steps.find((s) => s.id === selectedWorkflowStepId);
    if (currentlySelected) {
      return;
    }

    const runningStep = workflowExecution.steps.find((s) => s.status === 'running');
    const pendingStep = workflowExecution.steps.find((s) => s.status === 'pending');
    const failedStep = workflowExecution.steps.find((s) => s.status === 'failed');
    const stepToSelect = runningStep || pendingStep || failedStep || workflowExecution.steps[0];
    if (stepToSelect) {
      setSelectedWorkflowStepId(stepToSelect.id);
    }
  }, [workflowExecution, selectedWorkflowStepId]);

  const startWorkflow = async () => {
    if (!selectedWorkflow || !testUserPrompt.trim()) {
      alert('Please select a workflow and enter a user prompt');
      return;
    }

    setWorkflowLoading(true);
    try {
      const headers = await tokenService.buildAuthHeaders();
      // Ensure model matches provider; fallback to first supported model if mismatched
      const allowedModels = getProviderModels(provider);
      const effectiveModel = allowedModels.includes(model) ? model : allowedModels[0] || model;
      if (effectiveModel !== model) {
        console.warn('Model/provider mismatch; falling back', {
          provider,
          requested: model,
          using: effectiveModel,
        });
      }

      const body: Record<string, any> = {
        workflowId: selectedWorkflow,
        goal: testUserPrompt,
        provider,
        model: effectiveModel,
        personId,
        tools: getEnabledTools(),
      };

      const documentOptions = attachedDocuments.map((doc) => ({
        id: doc.documentId,
        documentId: doc.documentId,
        name: doc.name,
        title: doc.name,
        url: doc.webViewUrl,
        webViewUrl: doc.webViewUrl,
        mimeType: doc.mimeType,
        modifiedTime: doc.modifiedTime,
        relevanceScore: doc.relevanceScore,
      }));

      if (documentOptions.length > 0) {
        body.options = {
          documents: documentOptions,
          selected_documents: documentOptions,
        };
      }

      const res = await fetch('/rpc/v1/agent/workflow/start', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        let detail = '';
        try {
          const errJson = await res.json();
          detail = errJson?.error_description || errJson?.error || '';
        } catch {
          // ignore
        }
        throw new Error(
          detail ? `Failed to start workflow: ${detail}` : 'Failed to start workflow'
        );
      }
      const execution: WorkflowExecution = await res.json();
      setWorkflowExecution(execution);

      // Start polling for updates (but don't auto-run steps)
      startPolling(execution.id);

      // Stop loading indicator since workflow is created
      setWorkflowLoading(false);
    } catch (err: any) {
      console.error('Failed to start workflow:', err);
      alert('Failed to start workflow: ' + err.message);
      setWorkflowLoading(false);
    }
  };

  const executeStep = async (stepId: string) => {
    if (!workflowExecution) return;

    console.log('[EXECUTE] Starting step execution:', stepId);

    // Ensure polling is active
    if (!pollingIntervalRef.current) {
      console.log('[EXECUTE] Starting polling...');
      startPolling(workflowExecution.id);
    } else {
      console.log('[EXECUTE] Polling already active');
    }

    // Optimistically update step to "running"
    setWorkflowExecution({
      ...workflowExecution,
      steps: workflowExecution.steps.map((step) =>
        step.id === stepId ? { ...step, status: 'running' } : step
      ),
    });
    console.log('[EXECUTE] Optimistically set step to running');

    try {
      const headers = await tokenService.buildAuthHeaders();

      // Fire and forget - don't wait for completion
      fetch(`/rpc/v1/agent/workflow/${workflowExecution.id}/execute`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stepId }),
      })
        .then(async (res) => {
          if (!res.ok) {
            console.error(`[EXECUTE] Failed to execute step ${stepId}:`, res.status);
            const text = await res.text();
            console.error('[EXECUTE] Error response:', text);
          } else {
            console.log('[EXECUTE] Step execution request accepted (202)');

            // Immediately connect to SSE stream for real-time events
            console.log('[SSE] Connecting to stream for step:', stepId);
            connectWorkflowStream(workflowExecution.id, stepId);
          }
        })
        .catch((err) => {
          console.error('[EXECUTE] Failed to execute step:', err);
        });

      // Polling mechanism will update the execution state as fallback
    } catch (err: any) {
      console.error('[EXECUTE] Failed to execute step:', err);
      alert('Failed to execute step: ' + err.message);
    }
  };

  const connectWorkflowStream = async (executionId: string, stepId: string) => {
    try {
      const headers = await tokenService.buildAuthHeaders();
      const url = `/rpc/v1/agent/workflow/${executionId}/stream/${stepId}`;

      console.log('[SSE] Opening stream connection:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...headers,
          Accept: 'text/event-stream',
        },
      });

      if (!response.ok) {
        console.error('[SSE] Failed to connect:', response.status, response.statusText);
        return;
      }

      console.log('[SSE] Connection opened successfully');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        console.error('[SSE] No reader available');
        return;
      }

      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('[SSE] Stream ended');
          break;
        }

        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages in buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        let eventType = '';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.substring(6).trim();
          } else if (line.startsWith('data:')) {
            eventData = line.substring(5).trim();
          } else if (line === '' && eventType && eventData) {
            // Complete event received
            console.log(`[SSE] Received ${eventType} event:`, eventData);

            try {
              const parsed = JSON.parse(eventData);

              if (eventType === 'end' || eventType === 'connected') {
                // Skip meta events
              } else {
                // Update workflow execution with streaming event
                setWorkflowExecution((prev) => {
                  if (!prev) return prev;

                  const updatedSteps = prev.steps.map((step) => {
                    if (step.id === stepId) {
                      const existingEvents = step.streamingEvents || [];
                      return {
                        ...step,
                        streamingEvents: [
                          ...existingEvents,
                          {
                            type: parsed.type || eventType,
                            data: parsed.data,
                          },
                        ],
                      };
                    }
                    return step;
                  });

                  return { ...prev, steps: updatedSteps };
                });
              }
            } catch (err) {
              console.error('[SSE] Failed to parse event data:', err, eventData);
            }

            // Reset for next event
            eventType = '';
            eventData = '';
          }
        }
      }
    } catch (err) {
      console.error('[SSE] Failed to connect to workflow stream:', err);
    }
  };

  const openEditResult = (stepId: string, currentResult: any) => {
    setEditingStepId(stepId);
    // Show full JSON for editing
    const content =
      typeof currentResult === 'string' ? currentResult : JSON.stringify(currentResult, null, 2);
    setEditedResult(content);
  };

  const saveEditedResult = () => {
    if (!workflowExecution || !editingStepId) return;

    // Update the step result in the execution
    const updatedExecution = {
      ...workflowExecution,
      steps: workflowExecution.steps.map((step) => {
        if (step.id === editingStepId) {
          // Try to parse as JSON, otherwise save as text
          let newResult: any;
          try {
            newResult = JSON.parse(editedResult);
          } catch {
            // If not valid JSON, wrap in an object
            newResult = { answer: editedResult, _edited: true };
          }
          return { ...step, result: newResult };
        }
        return step;
      }),
    };

    setWorkflowExecution(updatedExecution);
    setEditingStepId(null);
    setEditedResult('');
  };

  const toggleResultExpanded = (stepId: string) => {
    setExpandedResults((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  };

  // Prompt editing functions
  const openPromptEditor = (stepId: string) => {
    if (!workflowExecution) return;
    const step = workflowExecution.steps.find((s) => s.id === stepId);
    if (!step) return;

    setEditingPromptStepId(stepId);
    // Use edited prompt if exists, otherwise use original prompt (or description as fallback)
    const currentPrompt = editedPrompts[stepId] || step.prompt || step.description || '';
    setTempPromptEdit(currentPrompt);
    setPromptEditDialogOpen(true);
  };

  const closePromptEditor = () => {
    setPromptEditDialogOpen(false);
    setEditingPromptStepId(null);
    setTempPromptEdit('');
  };

  const savePromptEdit = () => {
    if (!editingPromptStepId || !tempPromptEdit.trim()) return;

    setEditedPrompts((prev) => ({
      ...prev,
      [editingPromptStepId]: tempPromptEdit,
    }));
    closePromptEditor();
  };

  // Inspector panel handlers
  const handleStepClick = (step: ExecutionStep) => {
    setSelectedStep(step);
    setInspectorOpen(true);
  };

  const handleInspectorClose = () => {
    setInspectorOpen(false);
    // Don't clear selectedStep immediately to allow closing animation
    setTimeout(() => setSelectedStep(null), 300);
  };

  const handleRetryStep = (step: ExecutionStep) => {
    // TODO: Implement retry logic
    console.log('Retry step:', step);
    alert('Retry functionality coming soon!');
  };

  const handleEditStepPrompt = (step: ExecutionStep) => {
    // TODO: Implement edit prompt logic
    console.log('Edit prompt for step:', step);
    alert('Edit prompt functionality coming soon!');
  };

  const findDependentSteps = (stepId: string): string[] => {
    if (!workflowExecution) return [];

    const dependent: string[] = [];
    const toCheck = [stepId];
    const checked = new Set<string>();

    while (toCheck.length > 0) {
      const current = toCheck.pop()!;
      if (checked.has(current)) continue;
      checked.add(current);

      // Find steps that depend on current step
      workflowExecution.steps.forEach((step) => {
        if (step.dependencies?.includes(current)) {
          dependent.push(step.id);
          toCheck.push(step.id);
        }
      });
    }

    return dependent;
  };

  const invalidateDependentSteps = (stepId: string) => {
    if (!workflowExecution) return;

    const dependentStepIds = findDependentSteps(stepId);
    if (dependentStepIds.length === 0) return;

    console.log('[INVALIDATE] Marking dependent steps as pending:', dependentStepIds);

    setWorkflowExecution({
      ...workflowExecution,
      steps: workflowExecution.steps.map((step) => {
        if (dependentStepIds.includes(step.id) && step.status === 'completed') {
          return {
            ...step,
            status: 'pending',
            // Keep result but mark it as stale
          };
        }
        return step;
      }),
    });
  };

  const executeStepWithPrompt = async (stepId: string) => {
    // Invalidate dependent steps before execution
    invalidateDependentSteps(stepId);

    // Execute with edited prompt, then mark as eligible for "Save as Default"
    await executeStep(stepId);
    if (editedPrompts[stepId]) {
      setStepsWithEditedPrompts((prev) => new Set(prev).add(stepId));
    }
  };

  const handlePlanDraftChange = React.useCallback((key: string, value: string) => {
    setPlanStepDrafts((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handlePlanDraftReset = React.useCallback((key: string) => {
    setPlanStepDrafts((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setCopiedDraftKey(null);
  }, []);

  const handlePlanDraftCopy = React.useCallback(async (key: string, value: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        setCopiedDraftKey(key);
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (err) {
      console.error('Failed to copy plan step draft:', err);
      alert('Copy failed—select the text and copy manually.');
    }
  }, []);

  // @ts-expect-error - Legacy function, will be removed
  const _extractReadableContent = (result: any): string => {
    if (!result) return '';
    if (typeof result === 'string') return result;

    // Try common top-level fields
    if (result.answer) return result.answer;
    if (result.content) return result.content;
    if (result.text) return result.text;
    if (result.summary) return result.summary;

    // Check messages array (common in chat-based results)
    if (result.messages && Array.isArray(result.messages) && result.messages.length > 0) {
      const lastMessage = result.messages[result.messages.length - 1];
      if (lastMessage.content) return lastMessage.content;
    }

    // Check planner trace for final response
    if (result.results?.plannerTrace && Array.isArray(result.results.plannerTrace)) {
      const lastTrace = result.results.plannerTrace[result.results.plannerTrace.length - 1];
      if (lastTrace?.decision?.final_response) {
        return lastTrace.decision.final_response;
      }
    }

    // Check direct plannerTrace
    if (result.plannerTrace && Array.isArray(result.plannerTrace)) {
      const lastTrace = result.plannerTrace[result.plannerTrace.length - 1];
      if (lastTrace?.decision?.final_response) {
        return lastTrace.decision.final_response;
      }
    }

    return '';
  };

  // @ts-expect-error - Legacy function, will be removed
  const _renderPlanSummary = (step: WorkflowStep, _isExpanded: boolean) => {
    const result: any = step.result ?? {};
    const planWrapper = result.plan;
    const strategy = planWrapper?.strategy;
    if (!strategy) return null;

    const goalUnderstanding = strategy.goal_understanding ?? '';
    const approach = strategy.approach ?? '';
    const estimatedIterations =
      typeof strategy.estimated_iterations === 'number' ? strategy.estimated_iterations : null;
    const riskFactors: string[] = Array.isArray(strategy.risk_factors)
      ? strategy.risk_factors.filter(Boolean)
      : [];
    const successCriteria: string[] = Array.isArray(strategy.success_criteria)
      ? strategy.success_criteria.filter(Boolean)
      : [];
    const steps: any[] = Array.isArray(strategy.steps) ? strategy.steps : [];

    const planValidation = result.planValidation?.validation ?? result.planValidation ?? null;
    const plannerModel = planWrapper?.plannerModel || planWrapper?.planner_model;
    const durationMs =
      typeof planWrapper?.durationMs === 'number'
        ? planWrapper.durationMs
        : typeof planWrapper?.duration_ms === 'number'
          ? planWrapper.duration_ms
          : null;

    const planStepResults: any[] = Array.isArray(result.planStepResults)
      ? result.planStepResults
      : [];

    const finalMessage = Array.isArray(result.messages) ? result.messages[0] : null;
    const finalContentRaw =
      typeof finalMessage?.content === 'string'
        ? finalMessage.content
        : finalMessage?.content != null
          ? (() => {
              try {
                return JSON.stringify(finalMessage.content, null, 2);
              } catch {
                return String(finalMessage.content);
              }
            })()
          : '';

    const finalResponseKey = `${step.id}-final-response`;
    const defaultFinalResponse =
      typeof finalContentRaw === 'string'
        ? (() => {
            try {
              const parsed = JSON.parse(finalContentRaw);
              return JSON.stringify(parsed, null, 2);
            } catch {
              return finalContentRaw;
            }
          })()
        : String(finalContentRaw ?? '');
    const finalResponseValue =
      defaultFinalResponse !== '' ? (planStepDrafts[finalResponseKey] ?? defaultFinalResponse) : '';
    const finalHasCustom =
      defaultFinalResponse !== '' ? planStepDrafts[finalResponseKey] !== undefined : false;
    const finalCopied = defaultFinalResponse !== '' && copiedDraftKey === finalResponseKey;

    let nextPlanIndex = -1;
    if (steps.length > 0) {
      for (let i = 0; i < steps.length; i += 1) {
        if (!planStepResults[i]) {
          nextPlanIndex = i;
          break;
        }
      }
      if (nextPlanIndex === -1 && planStepResults.length < steps.length) {
        nextPlanIndex = planStepResults.length;
      }
      if (nextPlanIndex >= steps.length) {
        nextPlanIndex = -1;
      }
    }

    const nextPlanStep =
      nextPlanIndex >= 0 && nextPlanIndex < steps.length ? steps[nextPlanIndex] : null;
    const draftKey = nextPlanStep ? `${step.id}-next-${nextPlanIndex}` : '';
    const defaultDraft =
      nextPlanStep != null
        ? (() => {
            try {
              return JSON.stringify(nextPlanStep, null, 2);
            } catch {
              return '';
            }
          })()
        : '';
    const draftValue = nextPlanStep != null ? (planStepDrafts[draftKey] ?? defaultDraft) : '';
    const hasCustomDraft = nextPlanStep != null ? planStepDrafts[draftKey] !== undefined : false;
    const draftCopied = nextPlanStep != null && copiedDraftKey === draftKey;

    const workflowStepIndex =
      workflowExecution?.steps?.findIndex((workflowStep) => workflowStep.id === step.id) ?? -1;
    const workflowNextStep =
      workflowStepIndex >= 0 && workflowExecution?.steps
        ? workflowExecution.steps[workflowStepIndex + 1]
        : undefined;
    const workflowNextStatus = workflowNextStep?.status ?? '';
    // @ts-expect-error - Intentionally unused for future use
    const _workflowNextStepRunnable =
      workflowNextStep != null &&
      (workflowNextStatus === 'pending' || workflowNextStatus === 'failed');
    // @ts-expect-error - Intentionally unused for future use
    const _workflowNextStepRunning = workflowNextStatus === 'running';
    // @ts-expect-error - Intentionally unused for future use
    const _workflowNextStepCompleted = workflowNextStatus === 'completed';

    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Typography variant="subtitle2">Planner Strategy</Typography>
          {plannerModel && (
            <Chip size="small" variant="outlined" label={`Planner: ${plannerModel}`} />
          )}
          {typeof durationMs === 'number' && (
            <Chip
              size="small"
              variant="outlined"
              label={`Planner time: ${(durationMs / 1000).toFixed(1)}s`}
            />
          )}
          {estimatedIterations != null && (
            <Chip
              size="small"
              variant="outlined"
              label={`Est. iterations: ${estimatedIterations}`}
            />
          )}
        </div>

        {(goalUnderstanding || approach) && (
          <Box className="grid gap-2 md:grid-cols-2">
            {goalUnderstanding && (
              <Box className="rounded-md border border-gray-200 bg-white p-3">
                <Typography variant="caption" color="text.secondary">
                  Goal understanding
                </Typography>
                <Typography variant="body2" className="mt-1">
                  {goalUnderstanding}
                </Typography>
              </Box>
            )}
            {approach && (
              <Box className="rounded-md border border-gray-200 bg-white p-3">
                <Typography variant="caption" color="text.secondary">
                  Approach
                </Typography>
                <Typography variant="body2" className="mt-1">
                  {approach}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {(riskFactors.length > 0 || successCriteria.length > 0) && (
          <Box className="grid gap-2 md:grid-cols-2">
            {riskFactors.length > 0 && (
              <Box className="rounded-md border border-gray-200 bg-white p-3">
                <Typography variant="caption" color="text.secondary">
                  Risk factors
                </Typography>
                <div className="mt-1 flex flex-wrap gap-1">
                  {riskFactors.map((risk) => (
                    <Chip key={`risk-${risk}`} size="small" variant="outlined" label={risk} />
                  ))}
                </div>
              </Box>
            )}
            {successCriteria.length > 0 && (
              <Box className="rounded-md border border-gray-200 bg-white p-3">
                <Typography variant="caption" color="text.secondary">
                  Success criteria
                </Typography>
                <div className="mt-1 flex flex-wrap gap-1">
                  {successCriteria.map((criteria) => (
                    <Chip
                      key={`criteria-${criteria}`}
                      size="small"
                      variant="outlined"
                      label={criteria}
                    />
                  ))}
                </div>
              </Box>
            )}
          </Box>
        )}

        {steps.length > 0 && (
          <div className="flex flex-col gap-2">
            <Typography variant="subtitle2">Plan steps</Typography>
            {steps.map((planStep, idx) => {
              if (!planStep) return null;
              const stepNumber =
                typeof planStep.step_number === 'number' ? planStep.step_number : idx + 1;
              const requiredTools: string[] = Array.isArray(planStep.required_tools)
                ? planStep.required_tools.filter(Boolean)
                : [];
              const dependencies: number[] = Array.isArray(planStep.dependencies)
                ? planStep.dependencies.filter((d: any) => d != null)
                : [];
              const planStepResult =
                Array.isArray(planStepResults) && planStepResults[idx]
                  ? planStepResults[idx]
                  : null;
              const toolOutputs: any[] = Array.isArray(planStepResult?.toolOutputs)
                ? planStepResult.toolOutputs
                : [];
              const finalDirectiveText =
                typeof planStepResult?.finalDirective === 'string'
                  ? planStepResult.finalDirective.trim()
                  : '';

              return (
                <Box
                  key={`plan-step-${step.id}-${idx}`}
                  className={`rounded-md border p-3 ${
                    idx === nextPlanIndex
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Typography variant="subtitle2">
                      Step {stepNumber}: {planStep.description || 'Untitled step'}
                    </Typography>
                    <div className="flex flex-wrap items-center gap-1">
                      {idx === nextPlanIndex && (
                        <Chip size="small" color="primary" label="Next up" />
                      )}
                      {typeof planStep.confidence === 'number' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Confidence ${(planStep.confidence * 100).toFixed(0)}%`}
                        />
                      )}
                    </div>
                  </div>
                  {planStep.expected_outcome && (
                    <Typography variant="caption" color="text.secondary" className="mt-1 block">
                      Expected outcome: {planStep.expected_outcome}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" className="mt-1 block">
                    Dependencies: {dependencies.length > 0 ? dependencies.join(', ') : 'None'}
                  </Typography>
                  {requiredTools.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {requiredTools.map((tool) => (
                        <Chip
                          key={`plan-step-${step.id}-${idx}-tool-${tool}`}
                          size="small"
                          variant="outlined"
                          label={tool}
                        />
                      ))}
                    </div>
                  )}

                  {planStepResult && (toolOutputs.length > 0 || finalDirectiveText) && (
                    <Box className="mt-3 space-y-2">
                      {finalDirectiveText && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Result summary
                          </Typography>
                          <Typography
                            variant="body2"
                            className="mt-1 whitespace-pre-wrap text-gray-700"
                          >
                            {finalDirectiveText}
                          </Typography>
                        </Box>
                      )}

                      {toolOutputs.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" className="block">
                            Tool outputs
                          </Typography>
                          <div className="mt-1 space-y-2">
                            {toolOutputs.map((output, outputIdx) => (
                              <Paper
                                key={`plan-step-${step.id}-${idx}-tool-output-${outputIdx}`}
                                variant="outlined"
                                className="p-2 bg-gray-50"
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  className="font-semibold"
                                >
                                  {output?.name || 'tool'} #{outputIdx + 1}
                                </Typography>
                                {output?.args && (
                                  <div className="mt-1">
                                    <Typography variant="caption" color="text.secondary">
                                      Arguments
                                    </Typography>
                                    <pre className="text-xs whitespace-pre-wrap bg-white rounded p-2 border mt-1">
                                      {prettyPrint(output.args)}
                                    </pre>
                                  </div>
                                )}
                                {output?.output !== undefined && (
                                  <div className="mt-1">
                                    <Typography variant="caption" color="text.secondary">
                                      Output
                                    </Typography>
                                    <pre className="text-xs whitespace-pre-wrap bg-white rounded p-2 border mt-1">
                                      {prettyPrint(output.output)}
                                    </pre>
                                  </div>
                                )}
                                {output?.error && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    className="mt-1 block"
                                  >
                                    {output.error}
                                  </Typography>
                                )}
                              </Paper>
                            ))}
                          </div>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </div>
        )}

        {defaultFinalResponse !== '' && (
          <Box className="rounded-md border border-purple-200 bg-purple-50 p-3">
            <Typography variant="subtitle2">Final response (edit before reuse)</Typography>
            <Typography variant="caption" color="text.secondary" className="mt-1 block">
              This is the exact payload returned by the planner. Tweak it here, then copy it into
              your next flow or wherever it needs to go.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={finalResponseValue}
              onChange={(event) => handlePlanDraftChange(finalResponseKey, event.target.value)}
              sx={{ mt: 1 }}
            />
            <Box className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                size="small"
                startIcon={<Icon>content_copy</Icon>}
                onClick={() => handlePlanDraftCopy(finalResponseKey, finalResponseValue)}
              >
                Copy
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon>refresh</Icon>}
                disabled={!finalHasCustom}
                onClick={() => handlePlanDraftReset(finalResponseKey)}
              >
                Reset
              </Button>
              {finalCopied && (
                <Typography variant="caption" color="success.main">
                  Copied
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {nextPlanStep && (
          <Box className="rounded-md border border-blue-200 bg-blue-50 p-3">
            <Typography variant="subtitle2">Next step input</Typography>
            <Typography variant="caption" color="text.secondary" className="mt-1 block">
              Adjust the JSON before running the next step. Changes stay local—copy the payload if
              you need to paste it elsewhere.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={draftValue}
              onChange={(event) => handlePlanDraftChange(draftKey, event.target.value)}
              sx={{ mt: 1 }}
            />
            <Box className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                size="small"
                startIcon={<Icon>content_copy</Icon>}
                onClick={() => handlePlanDraftCopy(draftKey, draftValue)}
              >
                Copy
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Icon>refresh</Icon>}
                disabled={!hasCustomDraft}
                onClick={() => handlePlanDraftReset(draftKey)}
              >
                Reset
              </Button>
              {draftCopied && (
                <Typography variant="caption" color="success.main">
                  Copied
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {!nextPlanStep && steps.length > 0 && (
          <Box className="rounded-md border border-gray-200 bg-white p-3">
            <Typography variant="subtitle2">Next step input</Typography>
            <Typography variant="caption" color="text.secondary" className="mt-1 block">
              All steps are complete. Update the plan above if you need to rerun anything.
            </Typography>
          </Box>
        )}

        {planValidation && (
          <Box className="rounded-md border border-gray-200 bg-white p-3">
            <Typography variant="subtitle2">Validation</Typography>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              {typeof planValidation.confidence_score === 'number' && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Confidence ${Math.round(planValidation.confidence_score * 100)}%`}
                />
              )}
              {typeof planValidation.estimated_success_probability === 'number' && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Success ${Math.round(
                    planValidation.estimated_success_probability * 100
                  )}%`}
                />
              )}
              {typeof planValidation.is_feasible === 'boolean' && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={planValidation.is_feasible ? 'Feasible' : 'Not feasible'}
                  color={planValidation.is_feasible ? 'success' : 'error'}
                />
              )}
              {planValidation.required_tools_available === false && (
                <Chip size="small" color="error" label="Missing required tools" />
              )}
            </div>
            {planValidation.reasoning && (
              <Typography variant="body2" className="mt-2">
                {planValidation.reasoning}
              </Typography>
            )}
            {Array.isArray(planValidation.missing_information) &&
              planValidation.missing_information.length > 0 && (
                <Box className="mt-2">
                  <Typography variant="caption" color="text.secondary">
                    Missing information
                  </Typography>
                  <ul className="ml-4 mt-1 list-disc text-sm">
                    {planValidation.missing_information.map((info: string) => (
                      <li key={`missing-${info}`}>{info}</li>
                    ))}
                  </ul>
                </Box>
              )}
            {Array.isArray(planValidation.potential_blockers) &&
              planValidation.potential_blockers.length > 0 && (
                <Box className="mt-2">
                  <Typography variant="caption" color="text.secondary">
                    Potential blockers
                  </Typography>
                  <ul className="ml-4 mt-1 list-disc text-sm">
                    {planValidation.potential_blockers.map((blocker: any, idx: number) => (
                      <li key={`blocker-${blocker?.description || idx}`}>
                        {blocker?.description || 'Unknown blocker'}{' '}
                        {blocker?.severity ? `(${blocker.severity})` : ''}
                      </li>
                    ))}
                  </ul>
                </Box>
              )}
          </Box>
        )}
      </div>
    );
  };

  const approveStep = async (stepId: string, approved: boolean, feedback = '') => {
    if (!workflowExecution) return;

    try {
      const headers = await tokenService.buildAuthHeaders();
      const body = { stepId, approved, feedback };

      const res = await fetch(`/rpc/v1/agent/workflow/${workflowExecution.id}/approve`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error('Failed to approve step');
      const execution: WorkflowExecution = await res.json();
      setWorkflowExecution((prev) => mergeExecutionPreservingEvents(execution, prev));

      // Continue workflow after approval
      if (approved) {
        await continueWorkflow(execution.id);
      }
    } catch (err: any) {
      console.error('Failed to approve step:', err);
      alert('Failed to approve step: ' + err.message);
    }
  };

  const continueWorkflow = async (executionId: string) => {
    try {
      const headers = await tokenService.buildAuthHeaders();
      const res = await fetch(`/rpc/v1/agent/workflow/${executionId}/continue`, {
        method: 'POST',
        headers,
      });

      if (!res.ok) throw new Error('Failed to continue workflow');
      const execution: WorkflowExecution = await res.json();
      setWorkflowExecution((prev) => mergeExecutionPreservingEvents(execution, prev));
    } catch (err: any) {
      console.error('Failed to continue workflow:', err);
      alert('Failed to continue workflow: ' + err.message);
    }
  };

  const avgLatency =
    testRuns.length > 0
      ? Math.round(testRuns.reduce((sum, r) => sum + r.latency, 0) / testRuns.length)
      : 0;
  const successRate =
    testRuns.length > 0
      ? Math.round((testRuns.filter((r) => r.success).length / testRuns.length) * 100)
      : 0;

  const enabledToolsCount = getEnabledTools().length;
  const conversationToRender = React.useMemo(() => {
    if (!compareMode && pendingAssistant && streamingSupported) {
      return [...conversation, pendingAssistant];
    }
    return conversation;
  }, [compareMode, conversation, pendingAssistant, streamingSupported]);
  const showTimeline =
    !compareMode &&
    streamingSupported &&
    (isStreaming || streamingSteps.length > 0 || !!streamingError);

  return (
    <div className="flex flex-col gap-4 pb-8 px-6 w-full max-w-[2400px] mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Icon sx={{ fontSize: 28 }}>science</Icon>
          <Typography variant="h4">Agents Test Suite</Typography>
          <Chip
            label={`${enabledToolsCount} tool${enabledToolsCount !== 1 ? 's' : ''}`}
            size="small"
            color="primary"
          />
        </div>
        <div className="flex gap-2">
          <FormControlLabel
            control={
              <Switch checked={compareMode} onChange={(e) => setCompareMode(e.target.checked)} />
            }
            label="Compare Mode"
          />
          <FormControlLabel
            control={<Switch checked={mockMode} onChange={(e) => setMockMode(e.target.checked)} />}
            label="Mock Mode"
          />
          <FormControlLabel
            control={
              <Switch
                checked={streamingEnabled}
                disabled={mockMode}
                onChange={(e) => setStreamingEnabled(e.target.checked)}
              />
            }
            label="Streaming"
          />
        </div>
      </div>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
        <Tab label="Agentic" icon={<Icon>account_tree</Icon>} iconPosition="start" />
        <Tab label="Chat" icon={<Icon>chat</Icon>} iconPosition="start" />
        <Tab label="Prompts" icon={<Icon>list</Icon>} iconPosition="start" />
        <Tab label="Tools" icon={<Icon>build</Icon>} iconPosition="start" />
        <Tab label="Metrics" icon={<Icon>analytics</Icon>} iconPosition="start" />
      </Tabs>

      {activeTab === 1 && (
        <>
          <Paper className="p-3 flex flex-col gap-3 w-full">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Typography variant="h6">Registered Agent Steps</Typography>
              <div className="flex items-center gap-2">
                {agentStepsLoading && <CircularProgress size={16} />}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Icon>refresh</Icon>}
                  onClick={() => {
                    void fetchAgentSteps();
                  }}
                >
                  Refresh
                </Button>
              </div>
            </div>
            {agentStepsError && (
              <Typography variant="body2" color="error">
                {agentStepsError}
              </Typography>
            )}
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {agentSteps.map((step) => (
                <Box
                  key={step.id}
                  className="border border-divider rounded-md p-3 flex flex-col gap-2 bg-surface"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Typography variant="subtitle1">{step.name ?? step.id}</Typography>
                    <Chip label={step.id} size="small" variant="outlined" />
                    {step.supports_streaming ? (
                      <Chip label="Streaming" size="small" color="success" />
                    ) : (
                      <Chip label="Sync" size="small" variant="outlined" />
                    )}
                  </div>
                  {step.description && (
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  )}
                  {Array.isArray(step.tags) && step.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {step.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </div>
                  )}
                  {step.input_schema && (
                    <details>
                      <summary className="text-sm cursor-pointer select-none text-primary">
                        Input Schema
                      </summary>
                      <pre className="bg-surface-variant text-xs p-2 rounded overflow-x-auto">
                        {prettyPrint(step.input_schema)}
                      </pre>
                    </details>
                  )}
                  {step.output_schema && (
                    <details>
                      <summary className="text-sm cursor-pointer select-none text-primary">
                        Output Schema
                      </summary>
                      <pre className="bg-surface-variant text-xs p-2 rounded overflow-x-auto">
                        {prettyPrint(step.output_schema)}
                      </pre>
                    </details>
                  )}
                </Box>
              ))}
              {!agentStepsLoading && agentSteps.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No agent steps registered.
                </Typography>
              )}
            </div>
          </Paper>

          <Paper className="p-3 flex flex-col gap-3 w-full">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <FormControl fullWidth>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={provider}
                  label="Provider"
                  onChange={(e: any) => setProvider(String(e.target.value))}
                >
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="anthropic">Anthropic</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Model</InputLabel>
                <Select
                  value={model}
                  label="Model"
                  onChange={(e: any) => setModel(String(e.target.value))}
                >
                  {getProviderModels(provider).map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Person ID"
                value={personId}
                disabled
                helperText={personId ? '' : 'Login required'}
              />
            </div>

            {compareMode && (
              <>
                <Divider />
                <Typography variant="subtitle2" color="text.secondary">
                  Compare With:
                </Typography>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FormControl fullWidth>
                    <InputLabel>Provider</InputLabel>
                    <Select
                      value={compareProvider}
                      label="Provider"
                      onChange={(e: any) => setCompareProvider(String(e.target.value))}
                    >
                      <MenuItem value="openai">OpenAI</MenuItem>
                      <MenuItem value="anthropic">Anthropic</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Model</InputLabel>
                    <Select
                      value={compareModel}
                      label="Model"
                      onChange={(e: any) => setCompareModel(String(e.target.value))}
                    >
                      {getProviderModels(compareProvider).map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </>
            )}

            <TextField
              label="Message"
              value={message}
              onChange={(e: any) => setMessage(e.target.value)}
              placeholder="Ask the agent..."
              multiline
              rows={3}
            />

            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={saveCustomPrompt}
                  startIcon={<Icon>save</Icon>}
                >
                  Save Prompt
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearConversation}
                  startIcon={<Icon>delete</Icon>}
                  disabled={conversation.length === 0}
                >
                  Clear History
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="contained"
                  onClick={() => (compareMode ? runComparison() : runAgent())}
                  disabled={loading || !personId}
                  startIcon={<Icon>{compareMode ? 'compare' : 'play_arrow'}</Icon>}
                >
                  {loading ? 'Running...' : compareMode ? 'Compare' : 'Run'}
                </Button>
              </div>
            </div>
          </Paper>

          {conversation.length > 0 && (
            <Paper className="p-3 w-full">
              <div className="flex justify-between items-center mb-3">
                <Typography variant="h6">Conversation</Typography>
                <div className="flex gap-2">
                  <Button
                    size="small"
                    onClick={() => exportResults('markdown')}
                    startIcon={<Icon>file_download</Icon>}
                  >
                    Export MD
                  </Button>
                  <Button
                    size="small"
                    onClick={() => exportResults('json')}
                    startIcon={<Icon>file_download</Icon>}
                  >
                    Export JSON
                  </Button>
                </div>
              </div>

              {compareMode && compareResult ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Typography variant="subtitle2" className="mb-2">
                      {provider}/{model}
                      {conversation[conversation.length - 1]?.latency && (
                        <Chip
                          label={`${conversation[conversation.length - 1].latency}ms`}
                          size="small"
                          className="ml-2"
                        />
                      )}
                    </Typography>
                    <div className="border rounded p-3 bg-gray-50">
                      {conversation[conversation.length - 1]?.content}
                      {conversation[conversation.length - 1]?.documents &&
                        conversation[conversation.length - 1].documents!.length > 0 && (
                          <div className="mt-2">
                            <Typography variant="caption">
                              Documents: {conversation[conversation.length - 1].documents!.length}
                            </Typography>
                          </div>
                        )}
                    </div>
                  </div>

                  <div>
                    <Typography variant="subtitle2" className="mb-2">
                      {compareProvider}/{compareModel}
                      {compareResult.latency && (
                        <Chip label={`${compareResult.latency}ms`} size="small" className="ml-2" />
                      )}
                    </Typography>
                    <div className="border rounded p-3 bg-gray-50">
                      {compareResult.content}
                      {compareResult.documents && compareResult.documents.length > 0 && (
                        <div className="mt-2">
                          <Typography variant="caption">
                            Documents: {compareResult.documents.length}
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {showTimeline && (
                    <PhaseAccordion
                      steps={streamingSteps}
                      isStreaming={isStreaming}
                      error={streamingError}
                      onStepClick={handleStepClick}
                    />
                  )}
                  {conversationToRender.map((msg, idx) => {
                    const isUser = msg.role === 'user';
                    const isLive = Boolean(msg.isStreaming);
                    const waitingTools = msg.toolCallsInProgress?.length ?? 0;

                    return (
                      <div
                        key={`${msg.timestamp}-${idx}-${msg.role}`}
                        className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-3xl ${
                            isUser ? 'bg-blue-50' : 'bg-gray-50'
                          } rounded p-3`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Icon sx={{ fontSize: 16 }}>{isUser ? 'person' : 'smart_toy'}</Icon>
                            <Typography variant="caption" color="text.secondary">
                              {isUser
                                ? 'You'
                                : `${msg.provider ?? 'assistant'}/${msg.model ?? '-'}`}
                            </Typography>
                            {isLive && (
                              <>
                                <CircularProgress size={12} />
                                <Typography variant="caption" color="text.secondary">
                                  {msg.plannerIteration !== undefined
                                    ? `Planning (iteration ${msg.plannerIteration})…`
                                    : msg.streamingContent
                                      ? 'Generating response…'
                                      : 'Thinking…'}
                                </Typography>
                              </>
                            )}
                            {!isLive && msg.latency && (
                              <Chip label={`${msg.latency}ms`} size="small" />
                            )}
                            {msg.error && <Chip label="Error" color="error" size="small" />}
                          </div>

                          <div className="space-y-1">
                            <Typography variant="body2" className="whitespace-pre-wrap">
                              {msg.content || msg.streamingContent || ''}
                            </Typography>
                            {waitingTools > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                Waiting on {waitingTools} tool
                                {waitingTools > 1 ? 's' : ''}…
                              </Typography>
                            )}
                          </div>

                          {Array.isArray(msg.toolCalls) && msg.toolCalls.length > 0 && (
                            <div className="mt-2 pt-2 border-t space-y-2">
                              <Typography variant="caption" color="text.secondary">
                                🔧 {msg.toolCalls.length} tool call
                                {msg.toolCalls.length > 1 ? 's' : ''}
                              </Typography>
                              {msg.toolCalls.map((call: any, callIdx: number) => {
                                const callStatus: string =
                                  call?.status ??
                                  (call?.error ? 'error' : call ? 'complete' : 'pending');
                                const statusLabel =
                                  callStatus === 'executing'
                                    ? 'Running'
                                    : callStatus === 'error'
                                      ? 'Error'
                                      : callStatus === 'complete'
                                        ? 'Complete'
                                        : 'Pending';
                                return (
                                  <Paper
                                    key={`tool-${call?.name || call?.tool}-${callIdx}`}
                                    variant="outlined"
                                    className="p-2 bg-white"
                                  >
                                    <div className="flex items-center gap-2">
                                      {callStatus === 'executing' && <CircularProgress size={16} />}
                                      {callStatus === 'complete' && (
                                        <Icon sx={{ color: 'green' }}>check</Icon>
                                      )}
                                      {callStatus === 'error' && (
                                        <Icon sx={{ color: 'error.main' }}>error</Icon>
                                      )}
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        className="font-semibold"
                                      >
                                        #{callIdx + 1} {call?.name || call?.tool || 'Unknown tool'}
                                      </Typography>
                                      <Chip label={statusLabel} size="small" />
                                      {typeof call?.duration === 'number' && (
                                        <Chip
                                          label={`${Math.round(call.duration)}ms`}
                                          size="small"
                                        />
                                      )}
                                    </div>
                                    {call?.args && (
                                      <div className="mt-1">
                                        <Typography variant="caption" color="text.secondary">
                                          Args
                                        </Typography>
                                        <pre className="text-xs whitespace-pre-wrap bg-gray-50 rounded p-2 overflow-auto max-h-32">
                                          {prettyPrint(call.args)}
                                        </pre>
                                      </div>
                                    )}
                                    {call?.output && (
                                      <div className="mt-1">
                                        <Typography variant="caption" color="text.secondary">
                                          Output
                                        </Typography>
                                        <pre className="text-xs whitespace-pre-wrap bg-gray-50 rounded p-2 overflow-auto max-h-40">
                                          {prettyPrint(call.output)}
                                        </pre>
                                      </div>
                                    )}
                                    {call?.error && (
                                      <Typography
                                        variant="caption"
                                        color="error"
                                        className="block mt-1"
                                      >
                                        {call.error}
                                      </Typography>
                                    )}
                                  </Paper>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {conversationToRender.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Start a conversation to see agent responses here.
                    </Typography>
                  )}
                </div>
              )}
            </Paper>
          )}
        </>
      )}

      {/* Step configuration dialog */}
      <Dialog
        open={stepDialogOpen}
        onClose={() => {
          setStepDialogOpen(false);
          setStepDraft(createEmptyStep());
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {stepEditIndex !== null
            ? 'Edit Workflow Step'
            : stepInsertBeforeIndex !== null
              ? `Insert Step Before #${stepInsertBeforeIndex + 1}`
              : 'Add Workflow Step'}
        </DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-3 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <TextField
                label="Step Name"
                value={stepDraft.name}
                onChange={(e) => setStepDraft((prev) => ({ ...prev, name: e.target.value }))}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Agent Role</InputLabel>
                <Select
                  label="Agent Role"
                  value={stepDraft.agentType}
                  onChange={(e) =>
                    setStepDraft((prev) => ({
                      ...prev,
                      agentType: e.target.value,
                    }))
                  }
                >
                  {['researcher', 'analyzer', 'writer', 'reviewer', 'router', 'generic'].map(
                    (role) => (
                      <MenuItem key={role} value={role}>
                        {role}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormControl fullWidth>
                <InputLabel>Provider</InputLabel>
                <Select
                  label="Provider"
                  value={stepDraft.provider}
                  onChange={(e) => {
                    const providerValue = e.target.value as PlannerExecutorProvider;
                    const models = getProviderModels(providerValue);
                    setStepDraft((prev) => ({
                      ...prev,
                      provider: providerValue,
                      plannerModel: models[0] ?? prev.plannerModel,
                      executorModel: models[1] ?? models[0] ?? prev.executorModel,
                    }));
                  }}
                >
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="anthropic">Anthropic (Claude)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Planner Model</InputLabel>
                <Select
                  label="Planner Model"
                  value={stepDraft.plannerModel}
                  onChange={(e) =>
                    setStepDraft((prev) => ({
                      ...prev,
                      plannerModel: e.target.value,
                    }))
                  }
                >
                  {getProviderModels(stepDraft.provider).map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <FormControl fullWidth>
              <InputLabel>Executor Model</InputLabel>
              <Select
                label="Executor Model"
                value={stepDraft.executorModel}
                onChange={(e) =>
                  setStepDraft((prev) => ({
                    ...prev,
                    executorModel: e.target.value,
                  }))
                }
              >
                {getProviderModels(stepDraft.provider).map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Step Prompt"
              value={stepDraft.prompt}
              onChange={(e) => setStepDraft((prev) => ({ ...prev, prompt: e.target.value }))}
              multiline
              rows={6}
              fullWidth
              placeholder="Explain what this step should accomplish, required tool usage, and desired output format."
            />

            <div>
              <Typography variant="subtitle2" className="mb-2">
                Tools
              </Typography>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {[...availableTools, ...customTools]
                  .filter((t) => t.enabled)
                  .map((tool) => (
                    <FormControlLabel
                      key={tool.name}
                      control={
                        <Checkbox
                          checked={stepDraft.tools.includes(tool.name)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setStepDraft((prev) => {
                              const set = new Set(prev.tools);
                              if (checked) {
                                set.add(tool.name);
                              } else {
                                set.delete(tool.name);
                              }
                              return { ...prev, tools: Array.from(set) };
                            });
                          }}
                        />
                      }
                      label={tool.name}
                    />
                  ))}
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setStepDialogOpen(false);
              setStepDraft(createEmptyStep());
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveStepDraft}
            startIcon={<Icon>{stepEditIndex !== null ? 'save' : 'add'}</Icon>}
          >
            {stepEditIndex !== null ? 'Save Changes' : 'Add Step'}
          </Button>
        </DialogActions>
      </Dialog>

      {activeTab === 2 && (
        <Paper className="p-3">
          <div className="flex items-center justify-between mb-3">
            <Typography variant="h6">Prompts</Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<Icon>add</Icon>}
              onClick={openCreatePrompt}
            >
              Create Prompt
            </Button>
          </div>

          <div className="space-y-2 mb-4">
            <Typography variant="subtitle2">Database Prompts</Typography>
            {dbPromptsLoading && (
              <Typography variant="caption" color="text.secondary">
                Loading prompts...
              </Typography>
            )}
            {dbPromptsError && (
              <Typography variant="caption" color="error">
                {dbPromptsError}
              </Typography>
            )}
            {!dbPromptsLoading && !dbPromptsError && dbPrompts.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No prompts found
              </Typography>
            )}
            {!dbPromptsLoading &&
              !dbPromptsError &&
              dbPrompts.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-2 border rounded hover:bg-gray-50"
                >
                  <div>
                    <Typography variant="body2" fontWeight="bold">
                      {p.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.description || p.key}
                    </Typography>
                  </div>
                  <Button size="small" onClick={() => openEditPrompt(p)}>
                    Edit
                  </Button>
                </div>
              ))}
          </div>

          {customPrompts.length > 0 && (
            <div className="space-y-2">
              <Typography variant="subtitle2">Custom Prompts</Typography>
              {customPrompts.map((p, idx) => (
                <div
                  key={`custom-prompt-${p.name}-${idx}`}
                  className="flex items-center justify-between p-2 border rounded hover:bg-gray-50"
                >
                  <div>
                    <Typography variant="body2" fontWeight="bold">
                      {p.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.prompt}
                    </Typography>
                  </div>
                  <div className="flex gap-1">
                    <Button size="small" onClick={() => loadPrompt(p.prompt)}>
                      Load
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const updated = customPrompts.filter((_, i) => i !== idx);
                        setCustomPrompts(updated);
                        localStorage.setItem('agent-custom-prompts', JSON.stringify(updated));
                      }}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Paper>
      )}

      {activeTab === 3 && (
        <div className="space-y-4">
          <Paper className="p-3">
            <div className="flex justify-between items-center mb-3">
              <Typography variant="h6">Tools</Typography>
              <div className="flex gap-2">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={fetchDbTools}
                  startIcon={<Icon>refresh</Icon>}
                >
                  Refresh
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={openCreateTool}
                  startIcon={<Icon>add</Icon>}
                >
                  Create Tool
                </Button>
              </div>
            </div>

            {dbToolsLoading && (
              <Typography variant="caption" color="text.secondary">
                Loading tools...
              </Typography>
            )}
            {dbToolsError && (
              <Typography variant="caption" color="error">
                {dbToolsError}
              </Typography>
            )}
            {!dbToolsLoading && !dbToolsError && dbTools.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No tools found
              </Typography>
            )}
            {!dbToolsLoading && !dbToolsError && dbTools.length > 0 && (
              <div className="space-y-2">
                {dbTools.map((tool) => (
                  <div
                    key={tool.id}
                    className="flex items-center justify-between p-2 border rounded"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <Chip
                        label={tool.enabled ? 'enabled' : 'disabled'}
                        size="small"
                        color={tool.enabled ? 'success' : 'default'}
                      />
                      <div className="flex-1">
                        <Typography variant="body2" fontWeight="bold">
                          {tool.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {tool.description}
                        </Typography>
                      </div>
                      {tool.category && (
                        <Chip label={tool.category} size="small" variant="outlined" />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="small" onClick={() => openEditTool(tool)}>
                        Edit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Paper>

          <Paper className="p-3 space-y-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <Typography variant="h6">Tool Tester</Typography>
                <Typography variant="body2" color="text.secondary">
                  Run a tool with sample input and inspect the raw response that flows into the next
                  agent step.
                </Typography>
              </div>
              {toolTestLoading && (
                <Box className="md:w-48">
                  <LinearProgress />
                </Box>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto_auto] gap-3">
              <FormControl fullWidth>
                <InputLabel>Tool</InputLabel>
                <Select
                  value={toolTestSelection}
                  label="Tool"
                  onChange={(e) => setToolTestSelection(String(e.target.value))}
                >
                  {toolTestOptions.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<Icon>{toolTestLoading ? 'hourglass_top' : 'play_arrow'}</Icon>}
                disabled={toolTestLoading || !toolTestSelection}
                onClick={runToolTest}
              >
                {toolTestLoading ? 'Running...' : 'Run Test'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Icon>refresh</Icon>}
                disabled={toolTestLoading || !toolTestSelection}
                onClick={resetToolTestPayload}
              >
                Reset Payload
              </Button>
            </div>

            <TextField
              label="Tool Request (JSON)"
              value={toolTestPayload}
              onChange={(e) => setToolTestPayload(e.target.value)}
              multiline
              rows={8}
              fullWidth
              placeholder="{}"
            />

            {toolTestError && (
              <Typography variant="body2" color="error">
                {toolTestError}
              </Typography>
            )}
            {toolTestMeta && (
              <Typography variant="caption" color="text.secondary">
                {toolTestMeta.method} {toolTestMeta.url}
              </Typography>
            )}
            {toolTestResponse && (
              <div>
                <Typography variant="subtitle2" className="mb-1">
                  Response
                </Typography>
                <pre className="text-xs whitespace-pre-wrap bg-gray-50 rounded p-2 overflow-auto max-h-64">
                  {toolTestResponse}
                </pre>
              </div>
            )}
          </Paper>
        </div>
      )}

      {activeTab === 4 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Paper className="p-4">
              <Typography variant="h3">{testRuns.length}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total Runs
              </Typography>
            </Paper>
            <Paper className="p-4">
              <Typography variant="h3">{successRate}%</Typography>
              <Typography variant="body2" color="text.secondary">
                Success Rate
              </Typography>
            </Paper>
            <Paper className="p-4">
              <Typography variant="h3">{avgLatency}ms</Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Latency
              </Typography>
            </Paper>
          </div>

          <Paper className="p-3">
            <Typography variant="h6" className="mb-3">
              Recent Test Runs
            </Typography>
            <div className="space-y-2">
              {testRuns
                .slice(-10)
                .reverse()
                .map((run) => (
                  <div
                    key={run.id}
                    className="flex items-center justify-between p-2 border rounded"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Chip
                          label={run.success ? '✓' : '✗'}
                          color={run.success ? 'success' : 'error'}
                          size="small"
                        />
                        <Typography variant="body2">
                          {run.provider}/{run.model}
                        </Typography>
                        <Chip label={`${run.latency}ms`} size="small" />
                      </div>
                      <Typography variant="caption" color="text.secondary" className="block mt-1">
                        {run.prompt.substring(0, 60)}...
                      </Typography>
                    </div>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(run.timestamp).toLocaleTimeString()}
                    </Typography>
                  </div>
                ))}
              {testRuns.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No test runs yet
                </Typography>
              )}
            </div>
          </Paper>
        </div>
      )}

      {activeTab === 0 && (
        <div className="space-y-4">
          <Paper className="p-4 space-y-4">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div>
                <Typography variant="h6">Agentic Workflow Builder</Typography>
                <Typography variant="body2" color="text.secondary">
                  Build multi-step workflows with per-step planner/executor models, prompts, and
                  tool access.
                </Typography>
              </div>
              <div className="flex gap-2">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Icon>post_add</Icon>}
                  onClick={resetBuilder}
                >
                  New Workflow
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Icon>{workflowLoading ? 'hourglass_top' : 'save'}</Icon>}
                  onClick={saveWorkflowDefinition}
                  disabled={
                    workflowLoading ||
                    builderSteps.length === 0 ||
                    !builderKey.trim() ||
                    !builderName.trim()
                  }
                >
                  {workflowLoading
                    ? 'Saving...'
                    : builderMode === 'edit'
                      ? 'Update Workflow'
                      : 'Save Workflow'}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormControl fullWidth>
                <InputLabel>Available Workflows</InputLabel>
                <Select
                  value={builderWorkflowId ?? ''}
                  label="Available Workflows"
                  onChange={(e) => {
                    const id = e.target.value as string;
                    if (!id) {
                      resetBuilder();
                    } else {
                      loadWorkflowForEdit(id);
                    }
                  }}
                >
                  <MenuItem value="">Create new workflow</MenuItem>
                  {workflows.map((wf) => (
                    <MenuItem key={wf.id} value={wf.id}>
                      {wf.name} ({wf.key})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Workflow Key"
                value={builderKey}
                onChange={(e) => setBuilderKey(e.target.value)}
                helperText="Lowercase identifier (used in API calls)"
                fullWidth
              />
              <TextField
                label="Workflow Name"
                value={builderName}
                onChange={(e) => setBuilderName(e.target.value)}
                fullWidth
              />
              <TextField
                label="Description"
                value={builderDescription}
                onChange={(e) => setBuilderDescription(e.target.value)}
                multiline
                rows={2}
                fullWidth
              />
            </div>

            <TextField
              label="Workflow Goal / Context"
              value={builderGoal}
              onChange={(e) => setBuilderGoal(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Explain the outcome you expect (e.g. 'Summarize my team's Q4 accomplishments')"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormControl fullWidth>
                <InputLabel>Default Provider</InputLabel>
                <Select
                  value={builderDefaultProvider}
                  label="Default Provider"
                  onChange={(e) => {
                    const value = e.target.value as PlannerExecutorProvider;
                    setBuilderDefaultProvider(value);
                    const first = getProviderModels(value)[0] ?? '';
                    setBuilderDefaultModel(first);
                  }}
                >
                  <MenuItem value="openai">OpenAI</MenuItem>
                  <MenuItem value="anthropic">Anthropic (Claude)</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Default Executor Model</InputLabel>
                <Select
                  value={builderDefaultModel}
                  label="Default Executor Model"
                  onChange={(e) => setBuilderDefaultModel(e.target.value)}
                >
                  {getProviderModels(builderDefaultProvider).map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Typography variant="subtitle2">Steps ({builderSteps.length})</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Icon>add</Icon>}
                  onClick={() => openStepEditor(null)}
                >
                  Add Step
                </Button>
              </div>

              {builderSteps.length === 0 ? (
                <Paper variant="outlined" className="p-3 bg-blue-50">
                  <Typography variant="body2" color="text.secondary">
                    Add at least one agent step. Each step can call tools, plan actions, and hand
                    off results to later steps.
                  </Typography>
                </Paper>
              ) : (
                <div className="space-y-2">
                  {builderSteps.map((step, idx) => (
                    <Paper key={step.id || `step-${idx}`} variant="outlined" className="p-3">
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Chip size="small" label={`#${idx + 1}`} />
                            <Typography variant="subtitle2">
                              {step.name || `Step ${idx + 1}`}
                            </Typography>
                            <Chip size="small" variant="outlined" label={step.agentType} />
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Provider: ${step.provider}`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Planner: ${step.plannerModel || DEFAULT_PLANNER_MODEL}`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Executor: ${step.executorModel || builderDefaultModel}`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${step.tools.length} tool${step.tools.length === 1 ? '' : 's'}`}
                            />
                          </div>
                        </div>
                        <div className="flex gap-1 self-start">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              setStepMenuAnchor(e.currentTarget);
                              setStepMenuIndex(idx);
                            }}
                          >
                            <Icon>more_vert</Icon>
                          </IconButton>
                        </div>
                      </div>
                      <Typography
                        variant="body2"
                        className="mt-2 whitespace-pre-wrap text-gray-700"
                      >
                        {step.prompt}
                      </Typography>
                      {step.tools.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {step.tools.map((tool) => (
                            <Chip key={tool} size="small" label={tool} />
                          ))}
                        </div>
                      )}
                    </Paper>
                  ))}
                </div>
              )}
            </div>

            {/* Step Actions Menu */}
            <Menu
              anchorEl={stepMenuAnchor}
              open={Boolean(stepMenuAnchor)}
              onClose={() => {
                setStepMenuAnchor(null);
                setStepMenuIndex(null);
              }}
            >
              <MenuItem
                onClick={() => {
                  if (stepMenuIndex !== null) {
                    openStepEditor(stepMenuIndex);
                  }
                  setStepMenuAnchor(null);
                  setStepMenuIndex(null);
                }}
              >
                <Icon sx={{ mr: 1 }}>edit</Icon>
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (stepMenuIndex !== null) {
                    openInsertBeforeEditor(stepMenuIndex);
                  }
                  setStepMenuAnchor(null);
                  setStepMenuIndex(null);
                }}
              >
                <Icon sx={{ mr: 1 }}>add_circle_outline</Icon>
                Insert Before
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (stepMenuIndex !== null) {
                    deleteStep(stepMenuIndex);
                  }
                  setStepMenuAnchor(null);
                  setStepMenuIndex(null);
                }}
              >
                <Icon sx={{ mr: 1, color: 'error.main' }}>delete</Icon>
                <Typography color="error">Delete</Typography>
              </MenuItem>
            </Menu>

            <Divider sx={{ my: 2 }} />

            <div className="flex justify-center py-3">
              <Button
                variant="contained"
                size="large"
                onClick={() => setShowTestForm(!showTestForm)}
                disabled={!selectedWorkflow}
                startIcon={<Icon>{showTestForm ? 'expand_less' : 'play_arrow'}</Icon>}
              >
                {showTestForm ? 'Hide Test Form' : 'Test Workflow'}
              </Button>
            </div>

            {showTestForm && (
              <div className="space-y-3 pt-2">
                <Typography variant="subtitle2" color="text.secondary">
                  Test Workflow Execution
                </Typography>
                <TextField
                  label="User Prompt"
                  value={testUserPrompt}
                  onChange={(e) => setTestUserPrompt(e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Enter the user's request to test the workflow (e.g., 'Find relevant documents about OKRs and team sync')"
                  helperText="This simulates the actual user input that will be passed as the goal when the workflow runs"
                />
                <div className="flex flex-col gap-2">
                  <Typography variant="subtitle2">Attach Google Docs (optional)</Typography>
                  <DriveDocumentSearch
                    personId={personId}
                    onDocumentsSelected={handleDocumentsSelected}
                    onLoadingChange={setDocSearchLoading}
                    onErrorChange={setDocSearchError}
                    disabled={workflowLoading || !personId}
                  />
                  {docSearchError && (
                    <Typography variant="caption" color="error">
                      {docSearchError}
                    </Typography>
                  )}
                  {attachedDocuments.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {attachedDocuments.map((doc) => (
                        <Chip
                          key={doc.documentId}
                          size="small"
                          variant="outlined"
                          label={doc.name || doc.documentId}
                          onDelete={() => handleRemoveDocument(doc.documentId)}
                          onClick={() => {
                            if (doc.webViewUrl && typeof window !== 'undefined') {
                              window.open(doc.webViewUrl, '_blank', 'noopener,noreferrer');
                            }
                          }}
                          clickable={Boolean(doc.webViewUrl)}
                        />
                      ))}
                    </div>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No documents attached. Use Drive search above to add context for this workflow
                      run.
                    </Typography>
                  )}
                </div>
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <Typography variant="caption" color="text.secondary">
                      Workflow: {selectedWorkflow || 'None selected'}
                    </Typography>
                    {selectedWorkflow && !testUserPrompt.trim() && (
                      <Typography variant="caption" color="warning.main" display="block">
                        Enter a user prompt above to start testing
                      </Typography>
                    )}
                  </div>
                  <Button
                    variant="contained"
                    onClick={startWorkflow}
                    disabled={
                      workflowLoading ||
                      docSearchLoading ||
                      !selectedWorkflow ||
                      !testUserPrompt.trim()
                    }
                  >
                    {workflowLoading ? 'Starting...' : 'Start Test'}
                  </Button>
                </div>
              </div>
            )}
          </Paper>
          {workflowExecution?.steps &&
            (() => {
              const totalSteps = workflowExecution.steps.length;
              const completedSteps = workflowExecution.steps.filter(
                (step) => step.status === 'completed'
              ).length;
              const runningIndex = workflowExecution.steps.findIndex(
                (step) => step.status === 'running'
              );
              const activeStepNumber =
                totalSteps === 0
                  ? 0
                  : Math.min(totalSteps, completedSteps + (runningIndex >= 0 ? 1 : 0));
              const progressPercent =
                totalSteps === 0 ? 0 : Math.round((activeStepNumber / totalSteps) * 100);

              return (
                <Paper className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <Typography variant="h6">Workflow Execution</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {workflowExecution.id}
                      </Typography>
                    </div>
                    <Chip
                      label={workflowExecution.status.toUpperCase()}
                      color={
                        workflowExecution.status === 'completed'
                          ? 'success'
                          : workflowExecution.status === 'failed'
                            ? 'error'
                            : workflowExecution.status === 'paused'
                              ? 'warning'
                              : 'info'
                      }
                    />
                  </div>

                  {totalSteps > 0 && (
                    <Box className="mb-4">
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Progress: Step {activeStepNumber} of {totalSteps} ({progressPercent}%)
                      </Typography>
                      <LinearProgress variant="determinate" value={progressPercent} />
                    </Box>
                  )}

                  <Divider className="mb-4" />

                  {/* Two-column grid layout: Steps list (left) and Detail view (right) */}
                  <div className="grid grid-cols-1 xl:grid-cols-[minmax(320px,420px),1fr] gap-6">
                    {/* Left column: Simplified steps list */}
                    <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                      {workflowExecution.steps.map((step, index) => {
                        const isSelected = step.id === selectedWorkflowStepId;
                        const isRunning = step.status === 'running';
                        const isCompleted = step.status === 'completed';
                        const isFailed = step.status === 'failed';

                        return (
                          <Paper
                            key={`list-${step.id}`}
                            variant="outlined"
                            className={`p-3 cursor-pointer transition-all ${
                              isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                            } ${isRunning ? 'animate-pulse' : ''}`}
                            onClick={() => setSelectedWorkflowStepId(step.id)}
                          >
                            <div className="flex items-center gap-2">
                              <div className="flex-shrink-0">
                                {isRunning && <CircularProgress size={20} />}
                                {isCompleted && (
                                  <Icon sx={{ fontSize: 20, color: 'success.main' }}>
                                    check_circle
                                  </Icon>
                                )}
                                {isFailed && (
                                  <Icon sx={{ fontSize: 20, color: 'error.main' }}>error</Icon>
                                )}
                                {!isRunning && !isCompleted && !isFailed && (
                                  <Icon
                                    sx={{
                                      fontSize: 20,
                                      color: 'text.secondary',
                                    }}
                                  >
                                    radio_button_unchecked
                                  </Icon>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <Typography variant="body2" className="font-medium truncate">
                                  {index + 1}. {step.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  className="truncate block"
                                >
                                  {step.agentType || 'Agent'}
                                </Typography>
                              </div>
                              <Chip
                                size="small"
                                label={step.status}
                                color={
                                  isCompleted
                                    ? 'success'
                                    : isFailed
                                      ? 'error'
                                      : isRunning
                                        ? 'info'
                                        : 'default'
                                }
                              />
                            </div>
                          </Paper>
                        );
                      })}
                    </div>

                    {/* Right column: Detailed view of selected step */}
                    <div className="max-h-[800px] overflow-y-auto">
                      {workflowExecution.steps.map((step, index) => {
                        if (step.id !== selectedWorkflowStepId) return null;
                        // Determine if this step can be run
                        const previousStep = index > 0 ? workflowExecution.steps[index - 1] : null;
                        const canRun = !previousStep || previousStep.status === 'completed';
                        const isRunning = step.status === 'running';
                        const isCompleted = step.status === 'completed';
                        const isFailed = step.status === 'failed';
                        const isPending = step.status === 'pending';
                        // @ts-expect-error - Intentionally unused for future use
                        const _hasPlannerStrategy = Boolean(step.result?.plan?.strategy);
                        const streamingSummary = summarizeWorkflowStreaming(step.streamingEvents);
                        const streamingText = (streamingSummary.text || '').trim();
                        const hasStreamingEvents = (step.streamingEvents?.length ?? 0) > 0;
                        // Show streaming box if: streaming is supported AND (currently running OR we have events to show)
                        const showStreamingBox =
                          streamingSupported && (isRunning || hasStreamingEvents);
                        const isStreamingActive = isRunning && streamingSummary.active;
                        return (
                          <Paper key={step.id} variant="outlined" className="p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Typography variant="subtitle2">
                                    {index + 1}. {step.name}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={step.status}
                                    color={
                                      step.status === 'completed'
                                        ? 'success'
                                        : step.status === 'failed'
                                          ? 'error'
                                          : step.status === 'running'
                                            ? 'info'
                                            : step.status === 'waiting_approval'
                                              ? 'warning'
                                              : 'default'
                                    }
                                  />
                                  {step.agentType && (
                                    <Chip size="small" label={step.agentType} variant="outlined" />
                                  )}
                                  {step.resultKey && (
                                    <Chip
                                      size="small"
                                      label={`context: ${step.resultKey}`}
                                      variant="outlined"
                                    />
                                  )}
                                  {Array.isArray(step.allowedTools) &&
                                    step.allowedTools.length > 0 && (
                                      <Chip
                                        size="small"
                                        label={`tools: ${step.allowedTools.slice(0, 3).join(', ')}`}
                                        variant="outlined"
                                      />
                                    )}
                                  {Array.isArray(step.contextInjections) &&
                                    step.contextInjections.length > 0 && (
                                      <Chip
                                        size="small"
                                        label={`${step.contextInjections.length} ctx fields`}
                                        variant="outlined"
                                      />
                                    )}
                                </div>

                                {step.description && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    className="mb-2"
                                  >
                                    {step.description}
                                  </Typography>
                                )}

                                {showStreamingBox && (
                                  <Box className="mt-3 space-y-2">
                                    <Typography variant="caption" color="text.secondary">
                                      Live Agent Output
                                    </Typography>
                                    <PhaseAccordion
                                      steps={streamingSummary.steps}
                                      isStreaming={isStreamingActive}
                                      error={streamingSummary.error}
                                      onStepClick={handleStepClick}
                                    />
                                    {(streamingText || streamingSummary.error) && (
                                      <Paper
                                        variant="outlined"
                                        className="p-3 bg-gray-900 text-gray-100"
                                      >
                                        {streamingText && (
                                          <Typography
                                            variant="body2"
                                            className="whitespace-pre-wrap"
                                          >
                                            {streamingText}
                                          </Typography>
                                        )}
                                        {streamingSummary.error && (
                                          <Typography variant="caption" color="error.light">
                                            {streamingSummary.error}
                                          </Typography>
                                        )}
                                      </Paper>
                                    )}
                                  </Box>
                                )}

                                {step.error && (
                                  <Typography variant="body2" color="error" className="mb-2">
                                    Error: {step.error}
                                  </Typography>
                                )}

                                {step.result && (
                                  <div className="mt-2">
                                    <div className="flex items-center justify-between mb-1">
                                      <Typography variant="caption" color="text.secondary">
                                        Result:
                                      </Typography>
                                      <div className="flex gap-1">
                                        {step.result._edited && (
                                          <Chip
                                            size="small"
                                            label="Edited"
                                            color="warning"
                                            sx={{ height: 20 }}
                                          />
                                        )}
                                        <IconButton
                                          size="small"
                                          onClick={() => openEditResult(step.id, step.result)}
                                        >
                                          <Icon sx={{ fontSize: 16 }}>edit</Icon>
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={() => toggleResultExpanded(step.id)}
                                        >
                                          <Icon sx={{ fontSize: 16 }}>
                                            {expandedResults.has(step.id)
                                              ? 'expand_less'
                                              : 'expand_more'}
                                          </Icon>
                                        </IconButton>
                                      </div>
                                    </div>
                                    <Paper variant="outlined" className="p-3 mt-1 bg-gray-50">
                                      <ResultFormatter data={step.result} />
                                    </Paper>
                                  </div>
                                )}

                                {/* Action Section */}
                                <Box className="mt-4 pt-3 border-t">
                                  {!canRun && isPending && (
                                    <Paper
                                      variant="outlined"
                                      className="p-2 mb-3 bg-amber-50 border-amber-200"
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        className="flex items-center gap-1"
                                      >
                                        <Icon sx={{ fontSize: 16 }}>info</Icon>
                                        Complete step {index} first to unlock this step
                                      </Typography>
                                    </Paper>
                                  )}

                                  {/* Approval Actions - Highest Priority */}
                                  {step.status === 'waiting_approval' && (
                                    <Box className="space-y-2">
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        className="block mb-2"
                                      >
                                        <Icon
                                          sx={{
                                            fontSize: 14,
                                            verticalAlign: 'middle',
                                            mr: 0.5,
                                          }}
                                        >
                                          pending_actions
                                        </Icon>
                                        Action Required
                                      </Typography>
                                      <div className="flex gap-2">
                                        <Button
                                          variant="contained"
                                          color="success"
                                          startIcon={<Icon>check</Icon>}
                                          onClick={() => approveStep(step.id, true)}
                                          fullWidth
                                        >
                                          Approve
                                        </Button>
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          startIcon={<Icon>close</Icon>}
                                          onClick={() =>
                                            approveStep(step.id, false, 'Rejected by user')
                                          }
                                          fullWidth
                                        >
                                          Reject
                                        </Button>
                                      </div>
                                    </Box>
                                  )}

                                  {/* Primary Actions */}
                                  {(isPending || isFailed) && (
                                    <Box className="space-y-2">
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        className="block mb-2"
                                      >
                                        <Icon
                                          sx={{
                                            fontSize: 14,
                                            verticalAlign: 'middle',
                                            mr: 0.5,
                                          }}
                                        >
                                          play_circle
                                        </Icon>
                                        Primary Actions
                                      </Typography>
                                      <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={
                                          <Icon>{isFailed ? 'refresh' : 'play_arrow'}</Icon>
                                        }
                                        disabled={!canRun || isRunning}
                                        onClick={() => executeStep(step.id)}
                                        fullWidth
                                      >
                                        {isFailed ? 'Retry Step' : 'Run Step'}
                                      </Button>
                                      <Divider />
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        className="block mt-2 mb-1"
                                      >
                                        Secondary Actions
                                      </Typography>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<Icon>edit</Icon>}
                                        disabled={!canRun || isRunning}
                                        onClick={() => openPromptEditor(step.id)}
                                        fullWidth
                                      >
                                        Edit Prompt
                                      </Button>
                                    </Box>
                                  )}

                                  {/* Completed Step Actions */}
                                  {isCompleted && (
                                    <Box className="space-y-2">
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        className="block mb-2"
                                      >
                                        <Icon
                                          sx={{
                                            fontSize: 14,
                                            verticalAlign: 'middle',
                                            mr: 0.5,
                                          }}
                                        >
                                          settings
                                        </Icon>
                                        Step Management
                                      </Typography>
                                      <div className="flex gap-2">
                                        <Button
                                          variant="outlined"
                                          startIcon={<Icon>replay</Icon>}
                                          onClick={() => executeStep(step.id)}
                                          fullWidth
                                        >
                                          Rerun
                                        </Button>
                                        <Button
                                          variant="outlined"
                                          startIcon={<Icon>edit</Icon>}
                                          onClick={() => openPromptEditor(step.id)}
                                          fullWidth
                                        >
                                          Edit & Rerun
                                        </Button>
                                      </div>
                                    </Box>
                                  )}
                                </Box>
                              </div>

                              {step.completedAt && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  className="ml-2"
                                >
                                  {new Date(step.completedAt).toLocaleTimeString()}
                                </Typography>
                              )}
                            </div>
                          </Paper>
                        );
                      })}
                    </div>
                  </div>

                  {workflowExecution.error && (
                    <Paper variant="outlined" className="p-3 mt-3 bg-red-50">
                      <Typography variant="body2" color="error">
                        Workflow Error: {workflowExecution.error}
                      </Typography>
                    </Paper>
                  )}
                </Paper>
              );
            })()}
        </div>
      )}

      {/* Add Custom Tool Dialog */}
      <Dialog
        open={toolDialogOpen}
        onClose={() => setToolDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Custom Tool</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-3 pt-2">
            <TextField
              label="Tool Name"
              value={newToolName}
              onChange={(e: any) => setNewToolName(e.target.value)}
              placeholder="my_custom_tool"
              fullWidth
            />
            <TextField
              label="Description"
              value={newToolDesc}
              onChange={(e: any) => setNewToolDesc(e.target.value)}
              placeholder="What does this tool do?"
              fullWidth
            />
            <TextField
              label="JSON Schema"
              value={newToolSchema}
              onChange={(e: any) => setNewToolSchema(e.target.value)}
              multiline
              rows={8}
              fullWidth
              helperText="JSON Schema definition for tool parameters"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolDialogOpen(false)}>Cancel</Button>
          <Button onClick={addCustomTool} variant="contained">
            Add Tool
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit DB Tool Dialog */}
      <Dialog
        open={toolEditDialogOpen}
        onClose={() => setToolEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Tool</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-3 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <TextField
                label="Name"
                value={toolForm.name}
                onChange={(e: any) => setToolForm({ ...toolForm, name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Category"
                value={toolForm.category}
                onChange={(e: any) => setToolForm({ ...toolForm, category: e.target.value })}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={toolForm.enabled}
                    onChange={(e: any) => setToolForm({ ...toolForm, enabled: !!e.target.checked })}
                  />
                }
                label="Enabled"
              />
            </div>
            {selectedDbTool && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <TextField label="Tool ID" value={toolForm.id ?? ''} fullWidth disabled />
                <TextField
                  label="Created At"
                  value={formatTimestamp(toolForm.createdAt)}
                  fullWidth
                  disabled
                />
                <TextField
                  label="Updated At"
                  value={formatTimestamp(toolForm.updatedAt)}
                  fullWidth
                  disabled
                />
                <TextField label="Created By" value={toolForm.createdBy ?? ''} fullWidth disabled />
              </div>
            )}
            <TextField
              label="Base URL"
              value={toolForm.baseUrl}
              onChange={(e: any) => setToolForm({ ...toolForm, baseUrl: e.target.value })}
              fullWidth
              placeholder="https://example.com"
              helperText="Optional base endpoint used when invoking this tool"
            />
            <TextField
              label="Description"
              value={toolForm.description}
              onChange={(e: any) => setToolForm({ ...toolForm, description: e.target.value })}
              fullWidth
            />
            <TextField
              label="JSON Schema"
              value={toolForm.jsonText}
              onChange={(e: any) => setToolForm({ ...toolForm, jsonText: e.target.value })}
              multiline
              rows={14}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveEditTool}
            disabled={toolSaving}
            startIcon={<Icon>{toolSaving ? 'hourglass_top' : 'save'}</Icon>}
          >
            {toolSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create DB Tool Dialog */}
      <Dialog
        open={toolCreateDialogOpen}
        onClose={() => setToolCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Tool</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-3 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <TextField
                label="Name"
                value={toolForm.name}
                onChange={(e: any) => setToolForm({ ...toolForm, name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Category"
                value={toolForm.category}
                onChange={(e: any) => setToolForm({ ...toolForm, category: e.target.value })}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={toolForm.enabled}
                    onChange={(e: any) => setToolForm({ ...toolForm, enabled: !!e.target.checked })}
                  />
                }
                label="Enabled"
              />
            </div>
            <TextField
              label="Base URL"
              value={toolForm.baseUrl}
              onChange={(e: any) => setToolForm({ ...toolForm, baseUrl: e.target.value })}
              fullWidth
              placeholder="https://example.com"
              helperText="Optional base endpoint used when invoking this tool"
            />
            <TextField
              label="Description"
              value={toolForm.description}
              onChange={(e: any) => setToolForm({ ...toolForm, description: e.target.value })}
              fullWidth
            />
            <TextField
              label="JSON Schema"
              value={toolForm.jsonText}
              onChange={(e: any) => setToolForm({ ...toolForm, jsonText: e.target.value })}
              multiline
              rows={14}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveCreateTool}
            disabled={toolSaving || !toolForm.name}
            startIcon={<Icon>{toolSaving ? 'hourglass_top' : 'add'}</Icon>}
          >
            {toolSaving ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Prompt Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Prompt</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-3 pt-2">
            <TextField label="Key" value={selectedDbPrompt?.key || ''} disabled fullWidth />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <TextField
                label="Name"
                value={editMetaForm?.name || ''}
                onChange={(e: any) =>
                  setEditMetaForm((prev) => ({
                    ...(prev || { name: '', description: '', category: '' }),
                    name: e.target.value,
                  }))
                }
                fullWidth
              />
              <TextField
                label="Category"
                value={editMetaForm?.category || ''}
                onChange={(e: any) =>
                  setEditMetaForm((prev) => ({
                    ...(prev || { name: '', description: '', category: '' }),
                    category: e.target.value,
                  }))
                }
                fullWidth
              />
              <TextField
                label="Description"
                value={editMetaForm?.description || ''}
                onChange={(e: any) =>
                  setEditMetaForm((prev) => ({
                    ...(prev || { name: '', description: '', category: '' }),
                    description: e.target.value,
                  }))
                }
                fullWidth
              />
            </div>
            <TextField
              label="Content"
              value={editContent}
              onChange={(e: any) => setEditContent(e.target.value)}
              multiline
              rows={14}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveEditPrompt}
            disabled={editSaving}
            startIcon={<Icon>{editSaving ? 'hourglass_top' : 'save'}</Icon>}
          >
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Prompt Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Prompt</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-3 pt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField
                label="Key"
                value={createForm.key}
                onChange={(e: any) => setCreateForm({ ...createForm, key: e.target.value })}
                placeholder="team_discovery"
                fullWidth
              />
              <TextField
                label="Name"
                value={createForm.name}
                onChange={(e: any) => setCreateForm({ ...createForm, name: e.target.value })}
                fullWidth
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <TextField
                label="Category"
                value={createForm.category}
                onChange={(e: any) => setCreateForm({ ...createForm, category: e.target.value })}
                placeholder="research"
                fullWidth
              />
              <TextField
                label="Description"
                value={createForm.description}
                onChange={(e: any) => setCreateForm({ ...createForm, description: e.target.value })}
                fullWidth
              />
            </div>
            <TextField
              label="Content"
              value={createForm.content}
              onChange={(e: any) => setCreateForm({ ...createForm, content: e.target.value })}
              multiline
              rows={14}
              fullWidth
            />
            <TextField
              label="Changelog"
              value={createForm.changelog}
              onChange={(e: any) => setCreateForm({ ...createForm, changelog: e.target.value })}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveCreatePrompt}
            disabled={createSaving || !createForm.key || !createForm.name}
            startIcon={<Icon>{createSaving ? 'hourglass_top' : 'add'}</Icon>}
          >
            {createSaving ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Result Dialog */}
      <Dialog open={!!editingStepId} onClose={() => setEditingStepId(null)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit Step Result</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" className="mb-3">
            Edit the complete result JSON. This entire object will be passed to the next step.
          </Typography>
          <TextField
            multiline
            rows={20}
            fullWidth
            value={editedResult}
            onChange={(e) => setEditedResult(e.target.value)}
            placeholder="Enter result content..."
            variant="outlined"
            sx={{
              '& textarea': {
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" className="mt-2">
            Tip: Enter valid JSON. You can modify any part of the result - messages, tool calls,
            planner trace, etc.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingStepId(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEditedResult} startIcon={<Icon>save</Icon>}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Prompt Editor Dialog */}
      <Dialog open={promptEditDialogOpen} onClose={closePromptEditor} maxWidth="md" fullWidth>
        <DialogTitle>Edit Step Prompt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" className="mb-3">
            Edit the system prompt for this step. This determines what instructions the AI receives.
          </Typography>
          <TextField
            multiline
            rows={12}
            fullWidth
            value={tempPromptEdit}
            onChange={(e) => setTempPromptEdit(e.target.value)}
            placeholder="Enter step instructions..."
            variant="outlined"
            sx={{
              '& textarea': {
                fontSize: '0.9rem',
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" className="mt-2 block">
            {tempPromptEdit.length} characters
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePromptEditor}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              savePromptEdit();
              if (editingPromptStepId) {
                executeStepWithPrompt(editingPromptStepId);
              }
            }}
            disabled={!tempPromptEdit.trim()}
            startIcon={<Icon>play_arrow</Icon>}
          >
            Run with This Prompt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inspector Panel */}
      <InspectorPanel
        step={selectedStep}
        open={inspectorOpen}
        onClose={handleInspectorClose}
        onRetry={handleRetryStep}
        onEditPrompt={handleEditStepPrompt}
      />
    </div>
  );
}
